import Phaser from 'phaser'

import { Character, TEX, buildCharacterTextures } from '~/game/Character'
import { actionForLabel } from '~/game/actions'
import type { AgentStyle } from '~/game/agentCharacters'
import {
  AGENTBAY_TEX,
  COMMONS_TEX,
  LOUNGE_TEX,
  MEETING_TEX,
  buildAgentBayTextures,
  buildCommonsTextures,
  buildFurnitureTextures,
  buildLoungeTextures,
  buildMeetingTextures,
  deskTexKey,
} from '~/game/furniture'
import { RoomBoard } from '~/game/RoomBoard'
import { enableSfx, playSfx } from '~/game/sfx'
import {
  ENV_TEX,
  STYLES,
  buildEnvironmentTextures,
  buildingBounds,
  computeWalkways,
  getOfficeStyle,
  skyPhase,
  type StyleSpec,
  type Walkways,
} from '~/game/environment'
import { startOfficeCat } from '~/game/cat'
import { startClassroom } from '~/game/classroom'
import { startWaterCooler } from '~/game/watercooler'
import {
  HEADER,
  computeLayout,
  type RoomLayout,
  type VacantSeat,
  type WorldLayout,
} from '~/game/world'
import { isMeetingLive, parseApiTime, upcomingForPulse } from '~/data/meetings'
import type { Person, WorldSnapshot } from '~/data/types'
import type { EventBus, GameEvent } from '~/realtime/bus'
import { theme } from '~/theme'

const toInt = (hex: string): number =>
  Phaser.Display.Color.HexStringToColor(hex).color

/** Screen px the HUD nav bar covers at the top of the canvas — the camera fit
 *  must keep the office below it or the top row of rooms hides behind the bar. */
const HUD_TOP = 64

const HUMAN_DOT = toInt(theme.human)
const AGENT_DOT = toInt(theme.agent)
const TASK_DOT = toInt(theme.task)
const INSIGHT_DOT = toInt('#ffd86b')

export interface OfficeSceneData {
  snapshot: WorldSnapshot
  bus: EventBus
  onFocus: (userId: string | null) => void
  /** Clicking a room's header/board strip opens its panel (messages, tasks…). */
  onRoomFocus?: (pulseId: string | null) => void
  /** Clicking a "+ hire" desk opens the hire-an-agent dialog for that room. */
  onHire?: (pulseId: string) => void
  /** Clicking a server rack opens that office's in-game health card. */
  onOfficeFocus?: (officeId: string) => void
  /** Clicking a room's task corkboard opens its tasks panel. */
  onTasksFocus?: (pulseId: string) => void
  /** Clicking a room's notes shelf opens its notes panel. */
  onNotesFocus?: (pulseId: string) => void
  /** Mouse hovering a character (null = hover ended) — drives peek cards. */
  onHoverPerson?: (userId: string | null, x: number, y: number) => void
  /** Whether the signed-in user may hire agents (org owner). */
  canHire?: boolean
  /** Arrange mode: dragging moves rooms instead of panning/selecting. */
  arrangeMode?: boolean
  /** A room was dragged by (dx, dy) world px — persist and re-layout. */
  onRoomMoved?: (roomId: string, dx: number, dy: number) => void
  selfUserId?: string
}

/** HH:MM (local) for a meeting board. Accepts ISO or bare API datetimes. */
function fmtClock(value: string | null): string {
  if (!value) return ''
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value)
  const d = new Date(hasZone ? value : `${value.replace(' ', 'T')}Z`)
  if (Number.isNaN(d.getTime())) return ''
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

/** Pull plain text out of a Slate/JSON rich-text message body. */
function collectSlateText(node: unknown): string {
  if (node == null) return ''
  if (Array.isArray(node)) return node.map(collectSlateText).join('')
  if (typeof node === 'object') {
    const obj = node as Record<string, unknown>
    if (typeof obj.text === 'string') return obj.text
    if (Array.isArray(obj.children)) return collectSlateText(obj.children)
  }
  return ''
}

export class OfficeScene extends Phaser.Scene {
  private snapshot!: WorldSnapshot
  private bus!: EventBus
  private onFocus!: (userId: string | null) => void
  private onRoomFocus?: (pulseId: string | null) => void
  private onHire?: (pulseId: string) => void
  private onOfficeFocus?: (officeId: string) => void
  private onTasksFocus?: (pulseId: string) => void
  private onNotesFocus?: (pulseId: string) => void
  private onHoverPerson?: (userId: string | null, x: number, y: number) => void
  private hoverUserId: string | null = null
  private cursorInteractive = false
  private canHire = false
  private arrangeMode = false
  private onRoomMoved?: (roomId: string, dx: number, dy: number) => void
  private dragRoom?: {
    id: string
    grabX: number
    grabY: number
    outline: Phaser.GameObjects.Rectangle
  }
  private layout!: WorldLayout
  private readonly chars = new Map<string, Character>()
  private readonly personByUser = new Map<string, Person>()
  private readonly boards = new Map<string, RoomBoard>()
  // keyed by `${roomId}:${topicId}` — to flash a topic pill on a live message.
  private readonly topicPills = new Map<string, Phaser.GameObjects.Text>()
  private nightOverlay?: Phaser.GameObjects.Rectangle
  // ScaleManager is GAME-scoped (survives scene.restart) — keep the handler so
  // resetState can unbind it, or every restart would leak a resize listener.
  private nightResize?: (size: Phaser.Structs.Size) => void
  private cameraResize?: (size: Phaser.Structs.Size) => void
  private lastActivity?: { x: number; y: number; at: number }
  private hottestCenter?: { x: number; y: number }
  private worldFits = false
  private minZoom = 0.3
  private stage = { x: 0, y: 0, w: 0, h: 0 }
  private autoDirectorPause = 0
  private busOff?: () => void
  private waterCoolerStop?: () => void
  private classroomStop?: () => void
  private catStop?: () => void
  private walkways?: Walkways
  // Wall furniture per TEAM room: the task corkboard + the notes shelf.
  // Hit rects feed pointerup; gfx handles redraw on live events.
  private readonly taskBoards = new Map<
    string,
    { g: Phaser.GameObjects.Graphics; x: number; y: number; badge: Phaser.GameObjects.Text }
  >()
  private readonly noteShelves = new Map<
    string,
    { g: Phaser.GameObjects.Graphics; x: number; y: number; count: number }
  >()
  private readonly wallHits: Array<{
    kind: 'tasks' | 'notes'
    pulseId: string
    x: number
    y: number
    w: number
    h: number
  }> = []
  private readonly taskCounts = new Map<
    string,
    { open: number; doing: number; done: number; overdue: number }
  >()
  // The org's chosen office style template (picked in Arrange mode).
  private style: StyleSpec = STYLES.warm
  // In-world server racks, one per office box (created on first health report).
  private readonly racks = new Map<
    string,
    {
      leds: Phaser.GameObjects.Rectangle[]
      status: Phaser.GameObjects.Text
      tween?: Phaser.Tweens.Tween
    }
  >()
  private rackCount = 0
  // Cooperative busy-lock so the water cooler never overlaps a real animation.
  private readonly busyUsers = new Set<string>()
  private selfUserId?: string
  // keyed by `${userId}:${roomId}`
  private readonly seatPos = new Map<string, { x: number; y: number }>()
  // The name tag on an empty seat — hidden while that person is sitting there.
  private readonly vacantSeats = new Map<string, Phaser.GameObjects.Text>()
  // The room each placed user is currently IN — updated by real chat activity so
  // chatting in a room counts as moving there (no bounce back to the load seat).
  private readonly currentRoom = new Map<string, string>()
  // Agent looks (the role's emblem: HR, PA, EA, Steward ★, the staff tier's
  // red ★; the stored look: character, colour, hat, cape, badge; unplugged):
  // org data from the bus, not per-scene state, so resetState keeps them and
  // a rebuilt floor (a meeting went live) spawns everyone in their look
  // instead of waiting for the poll.
  private readonly hrUserIds = new Set<string>()
  private readonly personalUserIds = new Set<string>()
  private readonly eaUserIds = new Set<string>()
  private readonly stewardUserIds = new Set<string>()
  private readonly stewardStaffUserIds = new Set<string>()
  private readonly styleByUser = new Map<string, AgentStyle>()
  private readonly unpluggedUserIds = new Set<string>()

  constructor() {
    super('office')
  }

  init(data: OfficeSceneData): void {
    this.snapshot = data.snapshot
    this.bus = data.bus
    this.onFocus = data.onFocus
    this.onRoomFocus = data.onRoomFocus
    this.onHire = data.onHire
    this.onOfficeFocus = data.onOfficeFocus
    this.onTasksFocus = data.onTasksFocus
    this.onNotesFocus = data.onNotesFocus
    this.onHoverPerson = data.onHoverPerson
    this.canHire = data.canHire ?? false
    this.arrangeMode = data.arrangeMode ?? false
    this.onRoomMoved = data.onRoomMoved
    this.selfUserId = data.selfUserId
  }

  create(): void {
    // create() also runs on scene.restart (a meeting went live/ended → a fresh
    // snapshot). Phaser destroys all game objects + scene timers/tweens on the
    // restart's SHUTDOWN, but our instance Maps still hold stale references — so
    // reset all per-scene state here for a clean, idempotent rebuild.
    this.resetState()

    this.cameras.main.setBackgroundColor('#0c0c10')
    buildCharacterTextures(this)
    buildFurnitureTextures(this)
    buildMeetingTextures(this)
    buildLoungeTextures(this)
    buildCommonsTextures(this)
    buildAgentBayTextures(this)
    buildEnvironmentTextures(this)
    this.style = STYLES[getOfficeStyle(this.snapshot.organizationId)]
    this.layout = computeLayout(this.snapshot)
    for (const person of this.snapshot.people) {
      this.personByUser.set(person.userId, person)
    }

    this.setupStage()
    this.drawEnvironment()
    this.drawRooms()
    this.drawLounge()
    this.spawnCharacters()
    this.drawHireSpots()
    this.setupCamera()
    this.setupInput()
    this.setupNight()
    this.highlightHotRooms()

    this.busOff = this.bus.on((event) => this.handle(event))
    this.time.addEvent({
      delay: 4500,
      loop: true,
      callback: () => this.autoDirector(),
    })

    // Ambient water-cooler social — decorative only, online people, never moves
    // anyone in a meeting or mid real-animation. Stopped on shutdown.
    this.waterCoolerStop = startWaterCooler(this, {
      chars: this.chars,
      lounge: this.layout.lounge,
      isOnline: (id) => this.personByUser.get(id)?.presence === 'online',
      isUnavailable: (id) => {
        const p = this.layout.byUser[id]
        return !!p?.atMeetingTable || !!p?.meetingAtDesk
      },
      isBusy: (id) => this.busyUsers.has(id),
      markBusy: (id, on) => {
        if (on) this.busyUsers.add(id)
        else this.busyUsers.delete(id)
      },
    })

    // Classroom study visits — only when the org has an "Agent Training" pulse
    // (a name convention, not schema); LEARNING agents wander in to "study".
    const classroom = this.layout.rooms.find(
      (r) => r.category === 'TEAM' && /agent\s*training/i.test(r.name),
    )
    if (classroom) {
      this.decorateClassroom(classroom)
      this.classroomStop = startClassroom(this, {
        chars: this.chars,
        room: classroom,
        isUnavailable: (id) => {
          const p = this.layout.byUser[id]
          return !!p?.atMeetingTable || !!p?.meetingAtDesk
        },
        isBusy: (id) => this.busyUsers.has(id),
        markBusy: (id, on) => {
          if (on) this.busyUsers.add(id)
          else this.busyUsers.delete(id)
        },
      })
    }

    // The office cat — wanders the corridors, naps on the lounge rug.
    if (this.walkways) {
      this.catStop = startOfficeCat(this, {
        walkways: this.walkways,
        lounge: this.layout.lounge,
      })
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.busOff?.()
      this.waterCoolerStop?.()
      this.classroomStop?.()
      this.catStop?.()
    })
  }

  /** Wipe per-scene state so a scene.restart rebuilds cleanly (no stale chars,
   *  boards, seat markers, or busy-locks pointing at destroyed objects). */
  private resetState(): void {
    this.waterCoolerStop?.()
    this.waterCoolerStop = undefined
    this.classroomStop?.()
    this.classroomStop = undefined
    this.catStop?.()
    this.catStop = undefined
    this.walkways = undefined
    this.taskBoards.clear()
    this.noteShelves.clear()
    this.wallHits.length = 0
    this.taskCounts.clear()
    this.racks.clear()
    this.rackCount = 0
    this.busOff?.()
    this.busOff = undefined
    this.chars.clear()
    this.personByUser.clear()
    this.boards.clear()
    this.topicPills.clear()
    this.seatPos.clear()
    this.vacantSeats.clear()
    this.busyUsers.clear()
    this.currentRoom.clear()
    this.dragRoom = undefined
    this.lastActivity = undefined
    this.hottestCenter = undefined
    this.autoDirectorPause = 0
    this.nightOverlay = undefined
    if (this.nightResize) {
      this.scale.off('resize', this.nightResize)
      this.nightResize = undefined
    }
    if (this.cameraResize) {
      this.scale.off('resize', this.cameraResize)
      this.cameraResize = undefined
    }
    // The hovered character is about to be destroyed — a stationary mouse
    // would otherwise leave its peek card stranded past the restart.
    if (this.hoverUserId) {
      this.hoverUserId = null
      this.onHoverPerson?.(null, 0, 0)
    }
    if (this.cursorInteractive) {
      this.cursorInteractive = false
      this.input?.setDefaultCursor('default')
    }
  }

  /** The office sits on a bounded "board": a tiled floor with a soft vignette
   *  framing its edges, all in world space so the grid + frame zoom and pan
   *  together with the rooms. Beyond the board is plain void — the grid never
   *  spills past the frame. */
  private setupStage(): void {
    const { worldW, worldH } = this.layout
    const cam = this.cameras.main
    // The board matches the SCREEN aspect ratio (while still containing the
    // office + a margin), so fitting it to the viewport fills the screen
    // edge-to-edge — no void bars on the sides or top/bottom.
    const m = 70
    // Match the VISIBLE area's aspect (below the HUD bar), so fitting the
    // board under the bar still fills it edge-to-edge with no void strips.
    const aspect = cam.width / Math.max(120, cam.height - HUD_TOP)
    const targetW = worldW + m * 2
    const targetH = worldH + m * 2
    let sw: number
    let sh: number
    if (targetW / targetH > aspect) {
      sw = targetW
      sh = targetW / aspect
    } else {
      sh = targetH
      sw = targetH * aspect
    }
    const sx = worldW / 2 - sw / 2
    const sy = worldH / 2 - sh / 2
    this.stage = { x: sx, y: sy, w: sw, h: sh }

    const g = this.add.graphics().setDepth(-6)
    g.fillStyle(toInt('#14141b'), 1)
    g.fillRect(sx, sy, sw, sh)
    g.lineStyle(1, toInt('#20202b'), 1)
    for (let x = sx + 34; x < sx + sw; x += 34) g.lineBetween(x, sy, x, sy + sh)
    for (let y = sy + 34; y < sy + sh; y += 34) g.lineBetween(sx, y, sx + sw, y)

    const key = 'glass-stage-vignette'
    const cap = 1400
    const cw = Math.max(8, Math.min(cap, Math.round(sw)))
    const ch = Math.max(8, Math.min(cap, Math.round(sh)))
    if (this.textures.exists(key)) this.textures.remove(key)
    const canvas = this.textures.createCanvas(key, cw, ch)
    if (!canvas) return
    const ctx = canvas.getContext()
    const f = Math.min(cw, ch) * 0.16
    const edge = (x0: number, y0: number, x1: number, y1: number): CanvasGradient => {
      const grad = ctx.createLinearGradient(x0, y0, x1, y1)
      grad.addColorStop(0, 'rgba(6,6,10,0.72)')
      grad.addColorStop(1, 'rgba(6,6,10,0)')
      return grad
    }
    ctx.fillStyle = edge(0, 0, 0, f)
    ctx.fillRect(0, 0, cw, f)
    ctx.fillStyle = edge(0, ch, 0, ch - f)
    ctx.fillRect(0, ch - f, cw, f)
    ctx.fillStyle = edge(0, 0, f, 0)
    ctx.fillRect(0, 0, f, ch)
    ctx.fillStyle = edge(cw, 0, cw - f, 0)
    ctx.fillRect(cw - f, 0, f, ch)
    canvas.refresh()
    this.add.image(sx, sy, key).setOrigin(0).setDepth(-3).setDisplaySize(sw, sh)
  }

  /** The building itself: interior ground, outer walls with an entrance,
   *  and the corridor network tying every room's door onto one street grid.
   *  All geometry derives from the FINAL room rects, so arrange-mode drags
   *  re-flow the whole environment on the next redraw. */
  private drawEnvironment(): void {
    const rooms = this.layout.rooms
    if (rooms.length === 0) return
    const lounge = this.layout.lounge
    const b = buildingBounds(rooms, lounge)
    const g = this.add.graphics()

    // Sidewalk apron around the building, then the interior ground — one warm
    // mass so the rooms read as parts of a single place, not floating panels.
    const style = this.style
    g.fillStyle(style.apron, 1)
    g.fillRoundedRect(b.x - 20, b.y - 20, b.w + 40, b.h + 40, 18)
    g.fillStyle(style.ground, 1)
    g.fillRoundedRect(b.x, b.y, b.w, b.h, 12)

    // Corridors: streets under each room row + the spine down to the lounge.
    const ways = computeWalkways(rooms, lounge)
    this.walkways = ways
    for (const s of [...ways.streets, ...ways.stubs]) {
      this.add
        .tileSprite(s.x, s.y, s.w, s.h, style.pavement)
        .setOrigin(0, 0)
        .setAlpha(0.85)
    }
    // Walk-in: continue the spine from its end through the entrance.
    const spine = ways.streets[ways.streets.length - 1]
    const entranceCx = spine ? spine.x + spine.w / 2 : b.x + b.w / 2
    if (spine) {
      this.add
        .tileSprite(spine.x, spine.y + spine.h, spine.w, b.y + b.h - (spine.y + spine.h), style.pavement)
        .setOrigin(0, 0)
        .setAlpha(0.85)
    }
    for (const m of ways.mats) {
      this.add.image(m.x, m.y, ENV_TEX.mat).setOrigin(0.5, 0)
    }

    // Outer walls with a gap for the entrance (aligned to the spine).
    const WALL = 6
    const gapW = 84
    const gapL = Phaser.Math.Clamp(entranceCx - gapW / 2, b.x + 40, b.x + b.w - 40 - gapW)
    g.fillStyle(style.wall, 1)
    g.fillRect(b.x - WALL, b.y - WALL, b.w + WALL * 2, WALL) // top
    g.fillRect(b.x - WALL, b.y - WALL, WALL, b.h + WALL * 2) // left
    g.fillRect(b.x + b.w, b.y - WALL, WALL, b.h + WALL * 2) // right
    g.fillRect(b.x - WALL, b.y + b.h, gapL - (b.x - WALL), WALL) // bottom-left
    g.fillRect(gapL + gapW, b.y + b.h, b.x + b.w + WALL - (gapL + gapW), WALL) // bottom-right
    g.fillStyle(style.wallHi, 0.55)
    g.fillRect(b.x - WALL, b.y - WALL, b.w + WALL * 2, 1.5)

    // Windows along the top wall, showing the sky the night overlay implies —
    // day blue, dusk ember, night with a star and warm light spilling out.
    const phase = skyPhase()
    const sky = phase === 'day' ? 0x3b526e : phase === 'dusk' ? 0x8a5238 : 0x101828
    for (let wx = b.x + 70; wx < b.x + b.w - 90; wx += 160) {
      g.fillStyle(style.frame, 1)
      g.fillRect(wx - 2, b.y - WALL - 3, 24, 14)
      g.fillStyle(sky, 1)
      g.fillRect(wx, b.y - WALL - 1, 20, 10)
      g.fillStyle(0x2a241f, 1)
      g.fillRect(wx + 9, b.y - WALL - 1, 2, 10)
      if (phase === 'night') {
        g.fillStyle(0xe8e6df, 0.9)
        g.fillRect(wx + 4, b.y - WALL + 1, 1, 1)
        g.fillStyle(0xffd28a, 0.3)
        g.fillRect(wx, b.y - WALL + 6, 20, 3)
      } else if (phase === 'dusk') {
        g.fillStyle(0xffb36b, 0.45)
        g.fillRect(wx, b.y - WALL + 5, 20, 4)
      }
    }

    // Corridor greenery: planters spaced along the horizontal streets, kept
    // clear of the door mats; a bench just inside the entrance.
    for (const s of ways.streets.filter((st) => st.w > st.h)) {
      for (let px = s.x + 90; px < s.x + s.w - 40; px += 190) {
        if (ways.mats.some((m) => Math.abs(m.x - px) < 34)) continue
        this.add.image(px, s.y - 1, ENV_TEX.planter).setOrigin(0.5, 1)
      }
    }
    this.add
      .image(gapL + gapW + 52, b.y + b.h - 6, ENV_TEX.bench)
      .setOrigin(0.5, 1)

    // The world OUTSIDE the walls: greenery hugging the building, a paved
    // walk-up to the entrance, and a bike rack. Deterministic (seeded by org)
    // so the grounds don't reshuffle every rebuild.
    this.drawOutside(b, WALL, entranceCx, gapL, gapW)

    // The entrance: a wide mat, flanking plants, and a welcome sign.
    this.add
      .image(gapL + gapW / 2, b.y + b.h - 4, ENV_TEX.mat)
      .setOrigin(0.5, 0)
      .setScale(2.4, 1.6)
    this.drawPlant(gapL - 12, b.y + b.h - 6)
    this.drawPlant(gapL + gapW + 12, b.y + b.h - 6)
    this.add
      .text(gapL + gapW / 2, b.y + b.h + 16, '✦ welcome', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '10px',
        color: theme.textDim,
      })
      .setOrigin(0.5, 0)

    // Zone paint: label the meeting band on the floor.
    const meetings = rooms.filter((r) => r.category === 'MEETING')
    if (meetings.length > 0) {
      const left = Math.min(...meetings.map((r) => r.x))
      const right = Math.max(...meetings.map((r) => r.x + r.w))
      const bottom = Math.max(...meetings.map((r) => r.y + r.h))
      this.add
        .text((left + right) / 2, bottom + 30, 'M E E T I N G S', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '9px',
          color: theme.textDim,
        })
        .setOrigin(0.5)
        .setAlpha(0.55)
    }

    // The 1:1 wing: every DM room is a legit room in its own walled-off
    // corner of the building (Malek, 2026-07-11 — never hide them). The wing
    // is a BLOCK in the bottom-right, so its walls form an L: a horizontal
    // run above the block out to the right wall, and a vertical run down its
    // left side to the bottom wall, with the door gap in the vertical wall so
    // the corridor side walks straight in. Entrance plants + a painted zone
    // label match the meeting band's language.
    const dmWing = rooms.filter((r) => r.category === 'DM')
    if (dmWing.length > 0) {
      const L = Math.min(...dmWing.map((r) => r.x))
      const T = Math.min(...dmWing.map((r) => r.y))
      const R = Math.max(...dmWing.map((r) => r.x + r.w))
      const wallX = L - 24
      const wallY = T - 24
      const doorW = 70
      // Door: vertically centred on the block's first row of rooms.
      const doorT = Phaser.Math.Clamp(
        T + 18,
        wallY + 10,
        b.y + b.h - 10 - doorW,
      )
      g.fillStyle(style.wall, 1)
      // Horizontal wall: from the corner out to the building's right wall.
      g.fillRect(wallX, wallY, b.x + b.w - wallX, 6)
      // Vertical wall: from the corner down to the building's bottom wall,
      // skipped when the block spans the full width (phones) — the building's
      // own left wall already closes the zone.
      const hasSide = wallX > b.x + 12
      if (hasSide) {
        g.fillRect(wallX, wallY, 6, doorT - wallY)
        g.fillRect(wallX, doorT + doorW, 6, b.y + b.h - (doorT + doorW))
      }
      g.fillStyle(style.wallHi, 0.55)
      g.fillRect(wallX, wallY, b.x + b.w - wallX, 1.5)
      if (hasSide) {
        this.drawPlant(wallX - 10, doorT + 2)
        this.drawPlant(wallX - 10, doorT + doorW + 12)
      }
      this.add
        .text((Math.max(L, wallX + 12) + R) / 2, wallY + 9, '1 : 1   R O O M S', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '9px',
          color: theme.textDim,
        })
        .setOrigin(0.5, 0)
        .setAlpha(0.55)
    }
  }

  /** The grounds outside the walls: trees and bushes around the building, the
   *  walk-up path from the entrance, and a bike rack. Seeded by org id so the
   *  planting is stable; positions derive from the shell bounds so arranging
   *  rooms re-lands everything sensibly. */
  private drawOutside(
    b: { x: number; y: number; w: number; h: number },
    wall: number,
    entranceCx: number,
    gapL: number,
    gapW: number,
  ): void {
    let seed = 0
    const orgId = this.snapshot.organizationId
    for (let i = 0; i < orgId.length; i += 1) seed = (seed * 31 + orgId.charCodeAt(i)) | 0
    const rng = (): number => {
      // Tiny deterministic LCG — stable grounds per org.
      seed = (seed * 1103515245 + 12345) & 0x7fffffff
      return seed / 0x7fffffff
    }
    const green = (x: number, y: number): void => {
      const kind = rng()
      if (kind < 0.6) this.add.image(x, y, ENV_TEX.tree).setOrigin(0.5, 1).setDepth(3)
      else this.add.image(x, y, ENV_TEX.bush).setOrigin(0.5, 1).setDepth(3)
    }

    // Tree line along the top and down both sides (outside the sidewalk).
    for (let x = b.x + 30; x < b.x + b.w - 20; x += 120 + rng() * 90) {
      if (rng() < 0.25) continue
      green(x + rng() * 30, b.y - wall - 10 - rng() * 14)
    }
    for (let y = b.y + 60; y < b.y + b.h - 40; y += 130 + rng() * 100) {
      if (rng() < 0.3) continue
      green(b.x - wall - 22 - rng() * 16, y)
      green(b.x + b.w + wall + 22 + rng() * 16, y + 40)
    }

    // The walk-up: pavement continuing from the entrance out into the world,
    // bushes flanking it, and the bike rack.
    const walkTop = b.y + b.h + wall
    this.add
      .tileSprite(entranceCx - 9, walkTop, 18, 58, this.style.pavement)
      .setOrigin(0, 0)
      .setAlpha(0.85)
    this.add.image(entranceCx - 26, walkTop + 26, ENV_TEX.bush).setOrigin(0.5, 1).setDepth(3)
    this.add.image(entranceCx + 26, walkTop + 38, ENV_TEX.bush).setOrigin(0.5, 1).setDepth(3)
    this.add
      .image(gapL - 44, walkTop + 20, ENV_TEX.bikeRack)
      .setOrigin(0.5, 1)
      .setDepth(3)
    green(entranceCx + 90 + rng() * 40, walkTop + 40)
    green(entranceCx - 110 - rng() * 40, walkTop + 46)
    void gapW
  }

  /** Open-task tallies from the snapshot's task tree (LIST containers are not
   *  work; OVERDUE reads from the legacy enum). Rooms beyond the snapshot's
   *  prefetch cap start at zero — the GameView poller fills them in live. */
  private countsFromSnapshot(pulseId: string): {
    open: number
    doing: number
    done: number
    overdue: number
  } {
    const rows = (this.snapshot.tasksByPulse[pulseId] ?? []).filter(
      (t) => (t.type ?? 'TASK').toUpperCase() === 'TASK',
    )
    const counts = { open: 0, doing: 0, done: 0, overdue: 0 }
    for (const t of rows) {
      const s = (t.status ?? 'TODO').toUpperCase()
      if (s === 'COMPLETED') counts.done += 1
      else if (s === 'INPROGRESS') counts.doing += 1
      else if (s === 'OVERDUE') counts.overdue += 1
      else if (s !== 'CANCELLED') counts.open += 1
    }
    return counts
  }

  private static readonly CARD_COLORS = {
    doing: 0x5b8dd6,
    open: 0xe0a13a,
    done: 0x54d18c,
    overdue: 0xff5a5a,
  }

  /** The task corkboard: pinned mini-cards colored by status + an open badge.
   *  Redraws in place on live task events. */
  private drawTaskBoard(
    pulseId: string,
    x: number,
    y: number,
    counts: { open: number; doing: number; done: number; overdue: number },
  ): void {
    let board = this.taskBoards.get(pulseId)
    if (!board) {
      const g = this.add.graphics().setDepth(5)
      const badge = this.add
        .text(x + 27, y + 24, '', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '8px',
          color: theme.textDim,
        })
        .setOrigin(0.5, 0)
        .setDepth(5)
      board = { g, x, y, badge }
      this.taskBoards.set(pulseId, board)
      this.wallHits.push({ kind: 'tasks', pulseId, x, y: y - 2, w: 54, h: 28 })
    }
    const { g } = board
    g.clear()
    // Cork + frame.
    g.fillStyle(0x3a2c18, 1)
    g.fillRect(x - 2, y - 2, 58, 24)
    g.fillStyle(0x5a4326, 1)
    g.fillRect(x, y, 54, 20)
    // Pinned cards: doing, then open, then done — capped to the grid.
    const cells: number[] = []
    const push = (n: number, color: number) => {
      for (let i = 0; i < n && cells.length < 24; i += 1) cells.push(color)
    }
    push(counts.doing, OfficeScene.CARD_COLORS.doing)
    push(counts.open, OfficeScene.CARD_COLORS.open)
    push(counts.overdue, OfficeScene.CARD_COLORS.overdue)
    push(counts.done, OfficeScene.CARD_COLORS.done)
    cells.forEach((color, i) => {
      const col = i % 8
      const row = Math.floor(i / 8)
      g.fillStyle(0xffffff, 0.85)
      g.fillRect(x + 3 + col * 6.4, y + 2.5 + row * 6, 4.5, 4.5)
      g.fillStyle(color, 1)
      g.fillRect(x + 3 + col * 6.4, y + 3.5 + row * 6, 4.5, 3.5)
    })
    const openTotal = counts.open + counts.doing + counts.overdue
    board.badge.setText(
      openTotal > 0 ? `▤ ${openTotal} open` : cells.length > 0 ? '▤ tasks' : '▤ tasks',
    )
    board.badge.setColor(counts.overdue > 0 ? '#ff8a7a' : theme.textDim)
  }

  /** The notes shelf: book spines accumulate as the pulse writes things down. */
  private drawNotesShelf(
    pulseId: string,
    x: number,
    y: number,
    count: number,
    fresh = false,
  ): void {
    let shelf = this.noteShelves.get(pulseId)
    if (!shelf) {
      const g = this.add.graphics().setDepth(5)
      shelf = { g, x, y, count }
      this.noteShelves.set(pulseId, shelf)
      this.wallHits.push({ kind: 'notes', pulseId, x, y: y - 2, w: 54, h: 28 })
      this.add
        .text(x + 27, y + 24, '📚 notes', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '8px',
          color: theme.textDim,
        })
        .setOrigin(0.5, 0)
        .setDepth(5)
    }
    shelf.count = count
    const { g } = shelf
    g.clear()
    // Shelf frame + plank.
    g.fillStyle(0x3a2c18, 1)
    g.fillRect(x - 2, y - 2, 58, 24)
    g.fillStyle(0x2a1f16, 1)
    g.fillRect(x, y, 54, 20)
    g.fillStyle(0x4a3524, 1)
    g.fillRect(x, y + 17, 54, 3)
    // Book spines (one per note, capped) — stable colors by index.
    const SPINES = [0xb5654a, 0x4a7a5c, 0x5b6bb0, 0xc9a34a, 0x8a5a8f, 0x54868c]
    const books = Math.min(count, 10)
    for (let i = 0; i < books; i += 1) {
      g.fillStyle(SPINES[i % SPINES.length], 1)
      g.fillRect(x + 3 + i * 5, y + 3 + (i % 2), 3.6, 14 - (i % 2))
    }
    if (books === 0) {
      g.fillStyle(0xffffff, 0.08)
      g.fillRect(x + 3, y + 12, 48, 2)
    }
    if (fresh) {
      const glow = this.add
        .rectangle(x + 27, y + 9, 58, 24, 0xc9a34a, 0.25)
        .setDepth(6)
      this.tweens.add({
        targets: glow,
        alpha: 0,
        duration: 1400,
        onComplete: () => glow.destroy(),
      })
    }
  }

  /** A couple of personality props per room, hash-picked from the pulse id so
   *  each room keeps its look between rebuilds: a wall piece (clock or a
   *  poster in the room's accent) + a floor piece (bookshelf or cabinet) when
   *  there's width to spare. */
  private drawRoomProps(room: RoomLayout, floorY: number, accent: string): void {
    let h = 0
    for (let i = 0; i < room.id.length; i += 1) h = (h * 31 + room.id.charCodeAt(i)) | 0
    h = Math.abs(h)
    const g = this.add.graphics()

    // TEAM rooms' wall strip belongs to the task board + notes shelf now —
    // wall props (clock/poster) only dress the other room kinds.
    if (room.category === 'TEAM') {
      if (room.w >= 230) this.drawFloorProp(g, room, h)
      return
    }
    if (h % 2 === 0) {
      // Wall clock, left of the wall strip.
      const cx = room.x + 20
      const cy = floorY + 12
      g.fillStyle(0x3a332b, 1)
      g.fillCircle(cx, cy, 6)
      g.fillStyle(0xe8e6df, 1)
      g.fillCircle(cx, cy, 4.5)
      g.lineStyle(1, 0x2a241f, 1)
      g.lineBetween(cx, cy, cx, cy - 3)
      g.lineBetween(cx, cy, cx + 2.4, cy + 1)
    } else {
      // Framed poster in the room's accent.
      const px = room.x + room.w - 42
      const py = floorY + 5
      g.fillStyle(0x3a332b, 1)
      g.fillRect(px - 1, py - 1, 14, 18)
      g.fillStyle(toInt(accent), 0.75)
      g.fillRect(px, py, 12, 16)
      g.fillStyle(0xe8e6df, 0.5)
      g.fillRect(px + 2, py + 10, 8, 1)
      g.fillRect(px + 2, py + 12, 5, 1)
      g.fillCircle(px + 6, py + 5, 2.5)
    }

    if (room.w >= 230) this.drawFloorProp(g, room, h)
  }

  /** Floor prop (bookshelf or filing cabinet) in the bottom-left corner. */
  private drawFloorProp(
    g: Phaser.GameObjects.Graphics,
    room: RoomLayout,
    h: number,
  ): void {
    const bx = room.x + 46
    const by = room.y + room.h - 12
    if ((h >> 1) % 2 === 0) {
      // Bookshelf with a few colourful spines.
      g.fillStyle(0x4a3524, 1)
      g.fillRect(bx - 10, by - 24, 20, 24)
      g.fillStyle(0x2a1f16, 1)
      g.fillRect(bx - 8, by - 21, 16, 8)
      g.fillRect(bx - 8, by - 11, 16, 8)
      const spines = [0xb5654a, 0x4a7a5c, 0x5b6bb0, 0xc9a34a, 0x8a5a8f]
      for (let i = 0; i < 5; i += 1) {
        g.fillStyle(spines[(h + i) % spines.length], 1)
        g.fillRect(bx - 7 + i * 3, by - 20 + (i % 2), 2, 6 - (i % 2))
      }
      for (let i = 0; i < 4; i += 1) {
        g.fillStyle(spines[(h + i + 2) % spines.length], 1)
        g.fillRect(bx - 6 + i * 4, by - 10, 3, 6)
      }
    } else {
      // Filing cabinet.
      g.fillStyle(0x565c68, 1)
      g.fillRect(bx - 7, by - 20, 14, 20)
      g.fillStyle(0x6a7180, 1)
      g.fillRect(bx - 7, by - 20, 14, 1.5)
      g.fillStyle(0x3e434d, 1)
      g.fillRect(bx - 6, by - 14, 12, 1)
      g.fillRect(bx - 6, by - 8, 12, 1)
      g.fillStyle(0xc9d2e0, 1)
      g.fillRect(bx - 1.5, by - 17, 3, 1.5)
      g.fillRect(bx - 1.5, by - 11, 3, 1.5)
      g.fillRect(bx - 1.5, by - 5, 3, 1.5)
    }
  }

  /** Dress the Commons as a real lounge: a big soft rug under the seating,
   *  warm string lights along the wall, a floor lamp, and a coffee table in
   *  the clear band by the door. The people themselves sit in armchairs
   *  (see spawnCharacters) — nothing here says "workstation". */
  private drawCommonsProps(room: RoomLayout, floorY: number): void {
    const floorH = room.h - (floorY - room.y) - 6
    const cx = room.x + room.w / 2
    // Rug: scaled to the floor, under everything that lives in the room.
    const rugScale = Math.max(
      0.9,
      Math.min((room.w - 44) / 120, (floorH - 34) / 84),
    )
    this.add
      .image(cx, floorY + floorH / 2 + 4, LOUNGE_TEX.rug)
      .setScale(rugScale)
      .setDepth(2)
      .setAlpha(0.9)
    // String lights: warm bulbs swagged along the top of the wall.
    const g = this.add.graphics().setDepth(4)
    const bulbs = Math.max(3, Math.floor((room.w - 40) / 26))
    for (let i = 0; i < bulbs; i += 1) {
      const bx = room.x + 22 + (i * (room.w - 44)) / (bulbs - 1)
      const by = floorY + 5 + (i % 2 === 0 ? 0 : 3)
      g.fillStyle(0xf0d27a, 0.9)
      g.fillCircle(bx, by, 1.6)
      g.fillStyle(0xf0d27a, 0.18)
      g.fillCircle(bx, by, 4)
    }
    this.add
      .image(room.x + 20, floorY + 40, COMMONS_TEX.lamp)
      .setOrigin(0.5, 1)
      .setDepth(5)
    // A soft pool of lamp light that breathes.
    const glow = this.add
      .ellipse(room.x + 22, floorY + 42, 34, 14, 0xf0d27a, 0.1)
      .setDepth(3)
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.1, to: 0.2 },
      duration: 2600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    this.add
      .image(cx, room.y + room.h - 12, COMMONS_TEX.table)
      .setOrigin(0.5, 1)
      .setDepth(5)
  }

  /** Dress the Agent Bay as a machine room with a soul: violet neon along the
   *  wall, a humming server tower, and a holo core centrepiece. Idle agents
   *  stand on glowing charge pads (see spawnCharacters). */
  private drawAgentBayProps(room: RoomLayout, floorY: number): void {
    const accent = toInt(theme.agent)
    // Neon strip along the wall + its soft under-glow.
    const g = this.add.graphics().setDepth(4)
    g.fillStyle(accent, 0.75)
    g.fillRect(room.x + 12, floorY + 4, room.w - 24, 2)
    const neonGlow = this.add
      .rectangle(room.x + room.w / 2, floorY + 9, room.w - 28, 8, accent, 0.1)
      .setDepth(3)
    this.tweens.add({
      targets: neonGlow,
      alpha: { from: 0.08, to: 0.2 },
      duration: 2100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    // Server tower against the wall, with a blinking status LED on top.
    const towerX = room.x + 24
    this.add
      .image(towerX, floorY + 42, AGENTBAY_TEX.tower)
      .setOrigin(0.5, 1)
      .setDepth(5)
    const led = this.add
      .circle(towerX + 5, floorY + 13, 1.4, toInt('#54d18c'))
      .setDepth(6)
    this.tweens.add({
      targets: led,
      alpha: { from: 1, to: 0.15 },
      duration: 750,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    // Holo core centrepiece in the wall band, orb glow slowly pulsing.
    const holoX = room.x + room.w / 2
    const holo = this.add
      .image(holoX, floorY + 42, AGENTBAY_TEX.holo)
      .setOrigin(0.5, 1)
      .setDepth(5)
    const orbGlow = this.add
      .ellipse(holoX, floorY + 16, 26, 22, accent, 0.14)
      .setDepth(4)
    this.tweens.add({
      targets: orbGlow,
      scaleX: { from: 0.85, to: 1.2 },
      scaleY: { from: 0.85, to: 1.2 },
      alpha: { from: 0.16, to: 0.05 },
      duration: 1900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    this.tweens.add({
      targets: holo,
      y: { from: floorY + 42, to: floorY + 40 },
      duration: 2300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
  }

  private drawRooms(): void {
    const peoplePerRoom = new Map<string, number>()
    for (const placement of this.layout.placements) {
      peoplePerRoom.set(
        placement.roomId,
        (peoplePerRoom.get(placement.roomId) ?? 0) + 1,
      )
    }
    const pulseById = new Map(this.snapshot.pulses.map((p) => [p.id, p]))

    for (const room of this.layout.rooms) {
      const g = this.add.graphics()
      g.fillStyle(0x000000, 0.33)
      g.fillRoundedRect(room.x + 3, room.y + 6, room.w, room.h, 12)
      g.fillStyle(toInt(theme.bgRoom), 1)
      g.lineStyle(2, toInt(theme.border), 1)
      g.fillRoundedRect(room.x, room.y, room.w, room.h, 10)
      g.strokeRoundedRect(room.x, room.y, room.w, room.h, 10)

      const floorY = room.y + HEADER + room.boardH
      const floorH = room.h - (floorY - room.y) - 6
      g.fillStyle(toInt('#1c1a17'), 1)
      g.fillRoundedRect(room.x + 6, floorY, room.w - 12, floorH, 6)
      // Warm-studio materials: wood in team rooms, carpet in meeting rooms,
      // checker tile in the commons, circuit raised-floor in the agent bay;
      // DM booths keep their moody dark floor.
      const material =
        room.category === 'TEAM'
          ? this.style.team
          : room.category === 'MEETING'
            ? this.style.meeting
            : room.category === 'LOBBY'
              ? this.style.lobby
              : room.category === 'AGENTS'
                ? ENV_TEX.circuit
                : null
      if (material) {
        this.add
          .tileSprite(room.x + 8, floorY + 2, room.w - 16, floorH - 4, material)
          .setOrigin(0, 0)
      } else {
        g.fillStyle(toInt('#1c2029'), 1)
        g.fillRoundedRect(room.x + 6, floorY, room.w - 12, floorH, 6)
        g.fillStyle(toInt('#21232e'), 1)
        g.fillRoundedRect(room.x + 18, floorY + 22, room.w - 36, floorH - 26, 10)
      }

      const accent =
        room.category === 'TEAM'
          ? this.decorAccent(this.decorType(room.name))
          : room.category === 'DM'
            ? '#6f7bd6'
            : room.category === 'MEETING'
              ? '#e8b24a'
              : room.category === 'LOBBY'
                ? '#caa15e'
                : room.category === 'AGENTS'
                  ? theme.agent
                  : '#5f5e5a'
      g.fillStyle(toInt(accent), 1)
      g.fillRect(room.x + 6, room.y + 8, 3, 15)
      if (material) {
        // Skirting in the room's accent + glass-wall glints (it IS the Glass
        // Office): faint light strips down the side walls, posts in corners.
        g.fillStyle(toInt(accent), 0.4)
        g.fillRect(room.x + 8, floorY, room.w - 16, 2)
        g.fillStyle(0xffffff, 0.035)
        g.fillRect(room.x + 3, floorY, 3, floorH)
        g.fillRect(room.x + room.w - 6, floorY, 3, floorH)
        g.fillStyle(0x3a332b, 1)
        g.fillRect(room.x + 2, room.y + room.h - 5, 4, 4)
        g.fillRect(room.x + room.w - 6, room.y + room.h - 5, 4, 4)
      }
      if (room.category === 'LOBBY') {
        this.drawCommonsProps(room, floorY)
      } else if (room.category === 'AGENTS') {
        this.drawAgentBayProps(room, floorY)
      } else if (material && room.category !== 'MEETING') {
        this.drawRoomProps(room, floorY, accent)
      }

      // 1:1 rooms are the wing's compact rooms — smaller title, and crop long
      // counterpart names rather than letting them spill through the walls.
      const isDm = room.category === 'DM'
      const title =
        isDm && room.name.length > 13 ? `${room.name.slice(0, 12)}…` : room.name
      this.add.text(room.x + 12, room.y + 10, title, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: isDm ? '11.5px' : '13px',
        color: theme.cream,
      })
      const tag = isDm
        ? '1:1'
        : room.category === 'LOBBY'
          ? 'LOUNGE'
          : room.category === 'AGENTS'
            ? 'AI BAY'
            : room.category === 'TEAM'
              ? 'ROOM'
              : room.category
      this.add
        .text(room.x + room.w - 12, room.y + 12, tag, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: room.category === 'AGENTS' ? theme.agent : theme.textDim,
        })
        .setOrigin(1, 0)

      if (room.category === 'MEETING') {
        this.drawMeetingRoom(room)
        continue
      }

      const pulse = pulseById.get(room.id)
      const here = peoplePerRoom.get(room.id) ?? 0
      // Same filter as the corkboard badge — the raw tree length counts LIST
      // containers and completed rows, so header and board contradicted.
      const c = this.countsFromSnapshot(room.id)
      const openTasks = c.open + c.doing + c.overdue
      const unread = pulse?.unreadCount ?? 0
      // 1:1 rooms speak conversation, not occupancy — "0 here · 0 open tasks"
      // is team-room language and just reads as noise in the wing. The halls
      // (Commons / Agent Bay) speak presence: who's around vs actually awake.
      const isHall = room.category === 'LOBBY' || room.category === 'AGENTS'
      let stats: string[]
      if (isDm) {
        stats = []
      } else if (room.category === 'AGENTS') {
        stats = [`${here} agent${here === 1 ? '' : 's'} on standby`]
      } else if (room.category === 'LOBBY') {
        let awake = 0
        for (const p of this.layout.placements) {
          if (p.roomId !== room.id) continue
          if (this.personByUser.get(p.userId)?.presence === 'online') awake += 1
        }
        stats = [`${here} staff`, `${awake} online`]
      } else {
        stats = [`${here} here`]
      }
      if (!isHall && !isDm) {
        stats.push(`${openTasks} open tasks`)
      }
      if (!isHall && unread > 0) {
        stats.push(`${unread} unread`)
      }
      if (stats.length > 0) {
        this.add.text(room.x + 12, room.y + 27, stats.join('  ·  '), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: isDm ? '#ffd28a' : theme.textDim,
        })
      }

      // A soft green "active now" pulse on rooms whose real latest_update is
      // fresh (< 2h) — shows where work is actually happening, no fabrication.
      const lastUpdate = parseApiTime(pulse?.latestUpdate ?? null)
      if (!Number.isNaN(lastUpdate) && Date.now() - lastUpdate < 2 * 3600_000) {
        const live = this.add
          .circle(room.x + room.w - 14, room.y + 30, 3, toInt('#54d18c'))
          .setDepth(8)
        this.tweens.add({
          targets: live,
          alpha: { from: 1, to: 0.25 },
          scale: { from: 1, to: 1.5 },
          duration: 950,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        })
      }

      // Topic pills, each individually addressable so a live message in a topic
      // can flash its own pill (see flashTopic). Stop before overflowing.
      let topicX = room.x + 12
      for (const t of (pulse?.topics ?? []).slice(0, 5)) {
        const pill = this.add.text(topicX, room.y + 44, `#${t.name}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: theme.terracottaLite,
        })
        if (topicX + pill.width > room.x + room.w - 10) {
          pill.destroy()
          break
        }
        if (t.id) this.topicPills.set(`${room.id}:${t.id}`, pill)
        topicX += pill.width + 8
      }

      if (room.boardH > 0) {
        const board = new RoomBoard(
          this,
          room.x + 12,
          room.y + HEADER - 6,
          room.w - 24,
          room.boardH - 4,
        )
        this.boards.set(room.id, board)
      }
      this.drawPlant(room.x + room.w - 22, room.y + room.h - 16)
      // Bots don't drink — the bay's wall space belongs to its server tower.
      if (room.category !== 'AGENTS') {
        this.drawWaterCooler(room.x + room.w - 24, floorY + 28)
      }
      // A capped hall hides its sleepiest tail — sign it honestly.
      const hidden = this.layout.overflowByRoom[room.id] ?? 0
      if (hidden > 0) {
        this.add
          .text(room.x + room.w - 12, room.y + room.h - 8, `+${hidden} more`, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '10px',
            color: theme.textDim,
          })
          .setOrigin(1, 1)
          .setDepth(8)
      }
      if (room.category === 'TEAM') {
        this.drawDecor(room.x + 24, room.y + room.h - 14, this.decorType(room.name))
        this.drawCoffee(room.x + 22, floorY + 26)
        // The room's work walls: task corkboard (left) + notes shelf (right).
        const counts = this.countsFromSnapshot(room.id)
        this.taskCounts.set(room.id, counts)
        this.drawTaskBoard(room.id, room.x + 12, floorY + 2, counts)
        this.drawNotesShelf(room.id, room.x + room.w - 66, floorY + 2, 0)
      }
      if (room.category === 'DM') this.drawBooth(room, floorY, floorH)
    }
  }

  private drawBooth(room: RoomLayout, floorY: number, floorH: number): void {
    const cx = room.x + room.w / 2
    const cy = floorY + floorH / 2
    const g = this.add.graphics().setDepth(6)
    // A small 1:1 booth: two chairs facing a little table.
    g.fillStyle(toInt('#23252f'), 1)
    g.fillRoundedRect(cx - 13, cy - 6, 26, 12, 3)
    g.fillStyle(toInt('#2b2f3a'), 1)
    g.fillRoundedRect(cx - 25, cy - 7, 8, 14, 2)
    g.fillRoundedRect(cx + 17, cy - 7, 8, 14, 2)
    // No floor label — the header's 1:1 tag names the room kind now, and the
    // compact wing floor has no spare pixels.
  }

  private drawPlant(x: number, y: number): void {
    // The polished monstera sprite (same one the meeting rooms use).
    this.add
      .image(x, y + 8, MEETING_TEX.plant)
      .setOrigin(0.5, 1)
      .setDepth(4)
      .setScale(0.78)
  }

  private drawWaterCooler(x: number, y: number): void {
    this.add
      .image(x, y, MEETING_TEX.cooler)
      .setOrigin(0.5, 1)
      .setDepth(4)
      .setScale(0.85)
  }

  private drawCoffee(x: number, y: number): void {
    const g = this.add.graphics().setDepth(4)
    g.fillStyle(toInt('#222732'), 1)
    g.fillRect(x - 6, y - 13, 12, 13)
    g.fillStyle(toInt('#11151d'), 1)
    g.fillRect(x - 4, y - 10, 8, 4)
    g.fillStyle(toInt('#3a4150'), 1)
    g.fillRect(x - 6, y - 3, 12, 3)
    g.fillStyle(toInt('#c9735a'), 1)
    g.fillRect(x - 2, y - 6, 4, 3)
  }

  private decorType(
    name: string,
  ): 'server' | 'easel' | 'chart' | 'money' | 'board' {
    const n = name.toLowerCase()
    if (n.includes('design')) return 'easel'
    if (n.includes('invest')) return 'money'
    if (n.includes('growth') || n.includes('market') || n.includes('sales'))
      return 'chart'
    if (n.includes('ops') || n.includes('operation') || n.includes('infra'))
      return 'server'
    const set = ['board', 'chart', 'easel', 'server'] as const
    let sum = 0
    for (let i = 0; i < name.length; i += 1) sum += name.charCodeAt(i)
    return set[sum % set.length]
  }

  private decorAccent(
    type: 'server' | 'easel' | 'chart' | 'money' | 'board',
  ): string {
    return {
      server: '#378add',
      easel: '#d4537e',
      chart: '#54d18c',
      money: '#54d18c',
      board: '#e0a13a',
    }[type]
  }

  private drawDecor(
    x: number,
    y: number,
    type: 'server' | 'easel' | 'chart' | 'money' | 'board',
  ): void {
    const g = this.add.graphics().setDepth(4)
    if (type === 'server') {
      g.fillStyle(toInt('#1b1f29'), 1)
      g.fillRect(x - 9, y - 30, 18, 30)
      g.lineStyle(1, toInt('#3a4150'), 1)
      g.strokeRect(x - 9, y - 30, 18, 30)
      for (let r = 0; r < 4; r += 1) {
        const light = this.add
          .rectangle(x + 4, y - 26 + r * 7, 3, 3, toInt(r % 2 ? '#54d18c' : '#e0a13a'))
          .setDepth(5)
        this.tweens.add({
          targets: light,
          alpha: { from: 0.3, to: 1 },
          duration: 600 + r * 180,
          yoyo: true,
          repeat: -1,
        })
      }
    } else if (type === 'easel') {
      g.lineStyle(2, toInt('#6b4a2f'), 1)
      g.lineBetween(x - 8, y, x, y - 26)
      g.lineBetween(x + 8, y, x, y - 26)
      g.fillStyle(toInt('#efe7d4'), 1)
      g.fillRect(x - 11, y - 30, 22, 16)
      g.fillStyle(toInt('#d4537e'), 1)
      g.fillRect(x - 7, y - 26, 6, 6)
      g.fillStyle(toInt('#378add'), 1)
      g.fillRect(x + 2, y - 22, 7, 5)
    } else if (type === 'chart') {
      g.fillStyle(toInt('#11151d'), 1)
      g.fillRect(x - 11, y - 28, 24, 24)
      g.lineStyle(1, toInt('#3a4150'), 1)
      g.strokeRect(x - 11, y - 28, 24, 24)
      g.fillStyle(toInt('#54d18c'), 1)
      ;[6, 11, 8, 16].forEach((bh, i) => g.fillRect(x - 8 + i * 5, y - 6 - bh, 3, bh))
    } else if (type === 'money') {
      g.fillStyle(toInt('#11151d'), 1)
      g.fillRect(x - 11, y - 28, 24, 24)
      g.lineStyle(2, toInt('#54d18c'), 1)
      g.lineBetween(x - 8, y - 8, x - 2, y - 14)
      g.lineBetween(x - 2, y - 14, x + 4, y - 11)
      g.lineBetween(x + 4, y - 11, x + 10, y - 22)
      this.add
        .text(x - 9, y - 27, '$', {
          fontFamily: 'monospace',
          fontSize: '12px',
          color: '#54d18c',
        })
        .setDepth(5)
    } else {
      g.fillStyle(toInt('#e9e4d6'), 1)
      g.fillRect(x - 12, y - 28, 26, 20)
      g.lineStyle(1, toInt('#9b9a93'), 1)
      g.strokeRect(x - 12, y - 28, 26, 20)
      ;['#f2c14e', '#7fd1b9', '#e98aa8'].forEach((c, i) => {
        g.fillStyle(toInt(c), 1)
        g.fillRect(x - 9 + i * 8, y - 24, 6, 6)
      })
    }
  }

  private spawnCharacters(): void {
    for (const placement of this.layout.placements) {
      const person = this.personByUser.get(placement.userId)
      if (!person) continue
      this.seatPos.set(`${placement.userId}:${placement.roomId}`, {
        x: placement.homeX,
        y: placement.homeY,
      })

      // The person's seat furniture matches the room: an armchair in the
      // Commons lounge, a charge pad in the Agent Bay, otherwise their desk —
      // unless they're gathered at a shared conference table (no furniture).
      const seatCategory = this.layout.roomById[placement.roomId]?.category
      if (seatCategory === 'LOBBY') {
        // Armchair BEHIND the person (depth 9 < character 10) — they sit IN it.
        this.add
          .image(placement.homeX, placement.homeY + 2, COMMONS_TEX.chair)
          .setScale(1.35)
          .setDepth(9)
      } else if (seatCategory === 'AGENTS') {
        // Glowing charge pad under the agent's feet.
        this.add
          .image(placement.homeX, placement.homeY + 14, AGENTBAY_TEX.pad)
          .setScale(1.3)
          .setDepth(9)
      } else if (!placement.atMeetingTable) {
        this.add
          .image(placement.homeX, placement.homeY + 10, deskTexKey(placement.variant))
          .setScale(1.25)
          .setDepth(11)
      }

      const char = new Character(this, {
        userId: person.userId,
        name: person.name,
        isAgent: person.isAgent,
        isHrManager: this.hrUserIds.has(person.userId),
        isPersonal: this.personalUserIds.has(person.userId),
        isEa: this.eaUserIds.has(person.userId),
        isSteward: this.stewardUserIds.has(person.userId),
        isStewardStaff: this.stewardStaffUserIds.has(person.userId),
        style: this.styleByUser.get(person.userId) ?? null,
        isUnplugged: this.unpluggedUserIds.has(person.userId),
        presence: person.presence,
        x: placement.homeX,
        y: placement.homeY,
      })
      char.setDepth(10)
      this.chars.set(person.userId, char)
      // A sign when they're meeting at their own desk (shared rooms were full).
      if (placement.meetingAtDesk) char.setInMeeting(true)
    }

    // Every member who belongs to a room but is seated elsewhere gets an empty,
    // named seat waiting there (with varied furniture). When they arrive we hide
    // it (see sendToRoom), so an occupied seat never shows the empty marker too.
    for (const seat of this.layout.vacant) {
      const key = `${seat.userId}:${seat.roomId}`
      this.seatPos.set(key, { x: seat.homeX, y: seat.homeY })
      this.vacantSeats.set(key, this.drawVacantSeat(seat))
    }
  }

  private drawVacantSeat(seat: VacantSeat): Phaser.GameObjects.Text {
    // The desk is part of the room and ALWAYS stays put (the same desk this
    // person uses when present). Only the name tag — the "empty seat, waiting
    // for X" cue — hides once they sit down, so nothing disappears on arrival.
    this.add
      .image(seat.homeX, seat.homeY + 10, deskTexKey(seat.variant))
      .setScale(1.25)
      .setDepth(11)
      .setAlpha(0.94)
    if (seat.inMeetingRoom) {
      // Their desk sits empty while they're in the meeting room — sign it.
      return this.add
        .text(seat.homeX, seat.homeY + 24, '🎧 in meeting room', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '8px',
          color: '#1a120e',
          backgroundColor: '#e8b24a',
          padding: { x: 4, y: 1 },
        })
        .setOrigin(0.5)
        .setDepth(9)
    }
    const label = this.add
      .text(seat.homeX, seat.homeY + 26, seat.name.split(' ')[0], {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '8.5px',
        color: theme.terracottaLite,
      })
      .setOrigin(0.5)
      .setDepth(9)
    label.setAlpha(0.7)
    return label
  }

  /** Camera view carried across scene.restart (meeting events, arrange drags,
   *  style changes): a restart used to snap back to full zoom-out mid-demo. */
  private static savedCam?: { orgId: string; x: number; y: number; zoom: number }
  /** Last rendered view, snapshotted per frame. The SHUTDOWN handler must NOT
   *  touch cameras.main — the CameraManager's own shutdown listener registered
   *  first and has already destroyed it (reading it threw and WEDGED every
   *  scene.restart, killing arrange mode). */
  private lastCamView?: { x: number; y: number; zoom: number }

  private setupCamera(): void {
    this.fitCameraToViewport(false)
    // Restore where the user was looking before the restart (same org only —
    // an org switch starts fresh). Bounds/zoom re-clamped for the new layout.
    const saved = OfficeScene.savedCam
    if (saved && saved.orgId === this.snapshot.organizationId) {
      const cam = this.cameras.main
      const z = Phaser.Math.Clamp(saved.zoom, this.minZoom, 2.2)
      cam.setZoom(z)
      const { x: sx, y: sy, w: sw, h: sh } = this.stage
      const padX = Math.max(0, (cam.width / z - sw) / 2)
      const padY = Math.max(0, (cam.height / z - sh) / 2)
      const hudPad = HUD_TOP / z
      cam.setBounds(sx - padX, sy - padY - hudPad, sw + padX * 2, sh + padY * 2 + hudPad)
      cam.centerOn(saved.x, saved.y)
      this.worldFits = z <= this.minZoom + 0.001
    }
    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      const cam = this.cameras?.main
      if (!cam) return
      this.lastCamView = { x: cam.midPoint.x, y: cam.midPoint.y, zoom: cam.zoom }
    })
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (!this.lastCamView) return
      OfficeScene.savedCam = {
        orgId: this.snapshot.organizationId,
        ...this.lastCamView,
      }
    })
    // Scale.RESIZE live-resizes the canvas/camera on window resize + rotation,
    // but the fit math (minZoom, bounds) is otherwise frozen at the create-time
    // aspect — leaving the office unable to zoom out to whole, or stranded in a
    // strip with void bars, until a scene restart. Re-fit on resize, preserving
    // the user's current zoom.
    this.cameraResize = () => this.fitCameraToViewport(true)
    this.scale.on('resize', this.cameraResize)
  }

  /** Recompute minZoom + pan bounds for the current viewport. preserveZoom
   *  keeps the user's zoom (re-clamped) on a resize; false zooms to fit-all. */
  private fitCameraToViewport(preserveZoom: boolean): void {
    // The scale listener can fire in the gap between scene shutdown and the
    // next create (cameras already torn down) — never crash there.
    const cam = this.cameras?.main
    if (!cam) return
    const { x: sx, y: sy, w: sw, h: sh } = this.stage
    // Fit into the area BELOW the HUD bar, and centre there: the bar is an
    // opaque overlay, so anything the full-viewport fit put under it was lost.
    const contain = Math.min(cam.width / sw, (cam.height - HUD_TOP) / sh)
    const fit = Phaser.Math.Clamp(contain, 0.12, 1.6)
    this.minZoom = fit
    const zoom = preserveZoom ? Math.max(cam.zoom, fit) : fit
    this.worldFits = zoom <= fit + 0.001
    const centerX = preserveZoom ? cam.midPoint.x : sx + sw / 2
    const centerY = preserveZoom
      ? cam.midPoint.y
      : sy + sh / 2 - HUD_TOP / (2 * zoom)
    cam.setZoom(zoom)
    const padX = Math.max(0, (cam.width / zoom - sw) / 2)
    const padY = Math.max(0, (cam.height / zoom - sh) / 2)
    // Extra top headroom so the board's top edge can be panned below the bar.
    const hudPad = HUD_TOP / zoom
    cam.setBounds(sx - padX, sy - padY - hudPad, sw + padX * 2, sh + padY * 2 + hudPad)
    cam.centerOn(centerX, centerY)
  }

  private setupInput(): void {
    const cam = this.cameras.main
    const MIN_ZOOM = this.minZoom
    const MAX_ZOOM = 2.2
    // Track a second touch pointer so two-finger pinch works on phones/tablets.
    this.input.addPointer(1)
    let pinchDist = 0
    let downX = 0
    let downY = 0
    let gestureMoved = false // true once a pan/pinch happens, so it isn't a tap

    // Zoom keeping the screen point (ax, ay) anchored on the same world spot.
    // Computed from live scroll/zoom fields — cam.getWorldPoint reads the
    // render matrix, which only updates at preRender, so inside one input
    // handler its before/after are identical and the anchor drifts (the old
    // "pinch doesn't zoom where I pinch" bug).
    const anchorZoom = (nextZoom: number, ax: number, ay: number): void => {
      cam.stopFollow()
      const z0 = cam.zoom
      const z1 = Phaser.Math.Clamp(nextZoom, MIN_ZOOM, MAX_ZOOM)
      const wx = cam.scrollX + cam.width / 2 + (ax - cam.width / 2) / z0
      const wy = cam.scrollY + cam.height / 2 + (ay - cam.height / 2) / z0
      cam.setZoom(z1)
      cam.scrollX = wx - cam.width / 2 - (ax - cam.width / 2) / z1
      cam.scrollY = wy - cam.height / 2 - (ay - cam.height / 2) / z1
      this.worldFits = z1 <= MIN_ZOOM + 0.001
      this.autoDirectorPause = this.time.now + 9000
    }

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const p1 = this.input.pointer1
      const p2 = this.input.pointer2
      // Two fingers down → pinch to zoom (and don't also pan).
      if (p1?.isDown && p2?.isDown) {
        gestureMoved = true
        const dist = Phaser.Math.Distance.Between(p1.x, p1.y, p2.x, p2.y)
        if (pinchDist > 0 && dist > 0) {
          anchorZoom(
            cam.zoom * (dist / pinchDist),
            (p1.x + p2.x) / 2,
            (p1.y + p2.y) / 2,
          )
        }
        pinchDist = dist
        return
      }
      pinchDist = 0
      if (!pointer.isDown) return
      // Arrange mode with a room grabbed → drag the room's ghost, not the camera.
      if (this.dragRoom) {
        const w = cam.getWorldPoint(pointer.x, pointer.y)
        const room = this.layout.roomById[this.dragRoom.id]
        if (room) {
          this.dragRoom.outline.setPosition(
            room.x + (w.x - this.dragRoom.grabX),
            room.y + (w.y - this.dragRoom.grabY),
          )
        }
        if (Math.abs(pointer.x - downX) + Math.abs(pointer.y - downY) > 8) gestureMoved = true
        return
      }
      // One finger / mouse → drag to pan (manual control breaks any follow).
      cam.stopFollow()
      cam.scrollX -= (pointer.x - pointer.prevPosition.x) / cam.zoom
      cam.scrollY -= (pointer.y - pointer.prevPosition.y) / cam.zoom
      if (Math.abs(pointer.x - downX) + Math.abs(pointer.y - downY) > 8) gestureMoved = true
      this.autoDirectorPause = this.time.now + 9000
    })

    // Hover peek (mouse only): a light hit-test on move, throttled; the peek
    // card is pure HUD — clicking still goes to the canvas as before.
    let hoverThrottle = 0
    const clearHover = (): void => {
      if (this.hoverUserId !== null) {
        this.hoverUserId = null
        this.onHoverPerson?.(null, 0, 0)
      }
    }
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown || pointer.wasTouch) {
        clearHover()
        return
      }
      const now = this.time.now
      if (now - hoverThrottle < 90) return
      hoverThrottle = now
      const world = cam.getWorldPoint(pointer.x, pointer.y)
      let best: Character | undefined
      // Screen-space threshold (like the click snap): a fixed world radius is
      // a few pixels at fit-to-screen zoom, making the peek nearly untriggerable
      // exactly in the overview where it matters most.
      let bestDist = 26 / cam.zoom
      for (const char of this.chars.values()) {
        const d = Phaser.Math.Distance.Between(world.x, world.y, char.x, char.y - 12)
        if (d < bestDist) {
          best = char
          bestDist = d
        }
      }
      const id = best?.userId ?? null
      if (id !== this.hoverUserId) {
        this.hoverUserId = id
        this.onHoverPerson?.(id, pointer.x, pointer.y)
      }
      // Discoverability: the cursor turns pointer over everything clickable —
      // characters, room header strips, corkboards/shelves, hire desks. The
      // walls give no other affordance that they're an interface.
      let interactive = Boolean(best)
      if (!interactive) {
        for (const room of this.layout.rooms) {
          if (
            room.category === 'LOBBY' ||
            room.category === 'AGENTS' ||
            room.category === 'MEETING'
          )
            continue
          if (
            world.x >= room.x &&
            world.x <= room.x + room.w &&
            world.y >= room.y &&
            world.y <= room.y + HEADER + room.boardH
          ) {
            interactive = true
            break
          }
        }
      }
      if (!interactive) {
        for (const hit of this.wallHits) {
          if (
            world.x >= hit.x &&
            world.x <= hit.x + hit.w &&
            world.y >= hit.y &&
            world.y <= hit.y + hit.h
          ) {
            interactive = true
            break
          }
        }
      }
      if (!interactive && this.canHire) {
        for (const spot of this.layout.hireSpots) {
          if (Phaser.Math.Distance.Between(world.x, world.y, spot.x, spot.y) < 26) {
            interactive = true
            break
          }
        }
      }
      if (interactive !== this.cursorInteractive) {
        this.cursorInteractive = interactive
        this.input.setDefaultCursor(interactive ? 'pointer' : 'default')
      }
    })
    // Pointer leaving the canvas (onto a window frame, the HUD, or out of the
    // browser) stops mousemove delivery — without this the peek card strands
    // on screen indefinitely.
    this.input.on('gameout', clearHover)

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      enableSfx()
      clearHover()
      // A SECOND finger must not reset the tap guard: a two-finger tap (or a
      // brief rest mid-pan) would otherwise pass the clean-tap checks and
      // select/deselect at the second finger's position.
      const p2 = this.input.pointer2
      if (p2?.isDown && pointer.id === p2.id) {
        gestureMoved = true
        return
      }
      downX = pointer.x
      downY = pointer.y
      gestureMoved = false
      // Arrange mode: grabbing a room starts a room drag (ghost outline).
      if (this.arrangeMode && !this.dragRoom) {
        const w = cam.getWorldPoint(pointer.x, pointer.y)
        for (const room of this.layout.rooms) {
          if (w.x < room.x || w.x > room.x + room.w) continue
          if (w.y < room.y || w.y > room.y + room.h) continue
          const outline = this.add
            .rectangle(room.x, room.y, room.w, room.h)
            .setOrigin(0, 0)
            .setStrokeStyle(2, toInt('#8f88ff'), 0.95)
            .setFillStyle(toInt('#5b54c8'), 0.12)
            .setDepth(500)
          this.dragRoom = { id: room.id, grabX: w.x, grabY: w.y, outline }
          break
        }
      }
    })

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer, over: Phaser.GameObjects.GameObject[]) => {
      pinchDist = 0
      // Finish a room drag: snap the delta to the grid feel and persist it.
      if (this.dragRoom) {
        const drag = this.dragRoom
        this.dragRoom = undefined
        const w = cam.getWorldPoint(pointer.x, pointer.y)
        const snap = (v: number) => Math.round(v / 8) * 8
        const dx = snap(w.x - drag.grabX)
        const dy = snap(w.y - drag.grabY)
        drag.outline.destroy()
        if (Math.abs(dx) + Math.abs(dy) >= 8) {
          this.onRoomMoved?.(drag.id, dx, dy)
        }
        return
      }
      if (this.arrangeMode) return
      // Objects that handle their OWN clicks (server rack, cat) are marked
      // consumesClick — don't ALSO run the world hit-tests for those, or one
      // click opens two panels. Characters are interactive but handler-less
      // (their clicks route through the forgiving snap below), so a blanket
      // over.length check would swallow every dead-on person click.
      if (over.some((o) => o.getData?.('consumesClick'))) return
      // Only a clean tap (not a pan or pinch) selects.
      if (gestureMoved) return
      if (Math.abs(pointer.x - downX) + Math.abs(pointer.y - downY) > 10) return
      // Forgiving hit-testing: snap to the NEAREST character within ~44px on
      // screen (zoom-independent), so taps near a small/moving person register
      // instead of needing a pixel-perfect hit. Tap empty space to deselect.
      const world = cam.getWorldPoint(pointer.x, pointer.y)
      // Tapping a room's header/board strip opens the room panel — the real
      // conversation, tasks, and people. Checked BEFORE the forgiving character
      // snap: no character ever stands in the strip, but front-row characters
      // sit close enough beneath it that their reach would otherwise eat every
      // header tap in a dense room.
      for (const room of this.layout.rooms) {
        if (
          room.category === 'LOBBY' ||
          room.category === 'AGENTS' ||
          room.category === 'MEETING'
        )
          continue
        if (world.x < room.x || world.x > room.x + room.w) continue
        if (world.y < room.y || world.y > room.y + HEADER + room.boardH) continue
        this.onRoomFocus?.(room.id)
        this.focusCameraOnRoom(room)
        return
      }
      // The wall furniture: task corkboard / notes shelf open their panels.
      for (const hit of this.wallHits) {
        if (
          world.x >= hit.x &&
          world.x <= hit.x + hit.w &&
          world.y >= hit.y &&
          world.y <= hit.y + hit.h
        ) {
          const room = this.layout.roomById[hit.pulseId]
          if (room) this.focusCameraOnRoom(room)
          if (hit.kind === 'tasks') this.onTasksFocus?.(hit.pulseId)
          else this.onNotesFocus?.(hit.pulseId)
          return
        }
      }
      // The "+ hire an agent" desks — their slot is theirs alone, so a tight
      // radius wins over the (wider) character snap below.
      if (this.canHire) {
        for (const spot of this.layout.hireSpots) {
          if (Phaser.Math.Distance.Between(world.x, world.y, spot.x, spot.y) < 26) {
            this.onHire?.(spot.roomId)
            return
          }
        }
      }
      const reach = 44 / cam.zoom
      let best: Character | undefined
      let bestDist = reach
      for (const char of this.chars.values()) {
        const d = Phaser.Math.Distance.Between(world.x, world.y, char.x, char.y)
        if (d < bestDist) {
          bestDist = d
          best = char
        }
      }
      if (best) {
        this.onFocus(best.userId)
        return
      }
      // No one tapped — if you tapped a room where you have a reserved desk,
      // walk your character over to your chair there.
      if (this.goToMySeatAt(world.x, world.y)) return
      this.onFocus(null)
      this.onRoomFocus?.(null)
    })

    this.input.on(
      'wheel',
      (
        pointer: Phaser.Input.Pointer,
        _over: Phaser.GameObjects.GameObject[],
        _dx: number,
        dy: number,
      ) => {
        anchorZoom(cam.zoom - dy * 0.001, pointer.x, pointer.y)
      },
    )
  }

  private setupNight(): void {
    const cam = this.cameras.main
    const overlay = this.add.rectangle(0, 0, cam.width, cam.height, toInt('#0b0c14'))
    overlay.setOrigin(0).setScrollFactor(0).setDepth(1000)
    overlay.setAlpha(this.nightAlpha())
    this.nightOverlay = overlay
    this.nightResize = (size: Phaser.Structs.Size) => {
      overlay.setSize(size.width, size.height)
    }
    this.scale.on('resize', this.nightResize)
    this.time.addEvent({
      delay: 60000,
      loop: true,
      callback: () => this.nightOverlay?.setAlpha(this.nightAlpha()),
    })
  }

  private nightAlpha(): number {
    const hour = new Date().getHours()
    if (hour >= 20 || hour < 6) return 0.18
    if (hour >= 18 || hour < 8) return 0.08
    return 0
  }

  private handle(event: GameEvent): void {
    if (event.kind === 'camera') {
      this.zoomCamera(event.action)
      return
    }
    if (event.kind === 'presence') {
      const char = this.chars.get(event.userId)
      char?.setPresence(event.status)
      if (char) this.markActivity(char.x, char.y)
      return
    }
    if (event.kind === 'message') {
      const { userId, pulseId, topicId } = event
      if (!pulseId) return
      const room = this.layout.roomById[pulseId]
      if (!room) return
      playSfx('message')
      // Flash the topic pill the message landed in (if any) — happens even when
      // the sender isn't a placed character, so topic activity always shows.
      if (topicId) this.flashTopic(pulseId, topicId)
      const text = this.messageText(event.text)
      const char = userId ? this.chars.get(userId) : undefined
      if (char) {
        // Ambient (Showcase) chatter and DM-booth messages are transient visits
        // (speak + drift home). Only a REAL message in a proper room counts as
        // moving there — otherwise DMs would trap people in tiny booths and
        // Showcase would permanently shuffle the whole floor.
        const onSpeak = (): void => {
          char.say(text)
          this.boards
            .get(pulseId)
            ?.push('message', char.isAgent ? AGENT_DOT : HUMAN_DOT)
        }
        if (event.ambient || room.category === 'DM') {
          this.sendToRoom(char, room, onSpeak)
        } else {
          this.relocateTo(char, room, onSpeak)
        }
        this.markActivity(char.x, char.y)
      } else {
        this.boards.get(pulseId)?.push('message', HUMAN_DOT)
        this.markActivity(room.x + room.w / 2, room.y + room.h / 2)
      }
      return
    }
    if (event.kind === 'typing') {
      this.chars.get(event.userId)?.showTyping()
      return
    }
    if (event.kind === 'agentWork') {
      // Fresh work reported by the agent's box — float it as a thought.
      // Duty firings arrive as 'duty fired: <slug>' (AGENT_DUTIES_SPEC.md);
      // rephrase so the bubble reads as starting a scheduled responsibility.
      const char = this.chars.get(event.userId)
      if (char) {
        char.showThought(
          event.summary.startsWith('duty fired:')
            ? '⏰ on duty: ' + event.summary.slice(11).trim()
            : event.summary,
        )
        // The same act vocabulary as the dashboard/Nova sprites — the agent
        // physically performs its work on the floor (envelope, wrench...).
        char.perform(actionForLabel(event.summary))
        this.markActivity(char.x, char.y)
      }
      return
    }
    if (event.kind === 'agentMode') {
      const char = this.chars.get(event.userId)
      if (char) {
        char.setLearningMode(event.mode)
        char.setInboxDepth(event.inboxCount)
      }
      return
    }
    if (event.kind === 'agentNeedsYou') {
      this.chars.get(event.userId)?.setNeedsYou(event.on)
      return
    }
    if (event.kind === 'agentStyle') {
      // Full map each emit: a listed agent wears its look, the rest the classic robot.
      this.styleByUser.clear()
      for (const [id, style] of Object.entries(event.styles)) this.styleByUser.set(id, style)
      for (const char of this.chars.values()) {
        if (char.isAgent) char.setStyle(this.styleByUser.get(char.userId) ?? null)
      }
      return
    }
    if (event.kind === 'agentUnplugged') {
      // Full set each emit: listed agents unplug, the rest plug back in.
      this.unpluggedUserIds.clear()
      for (const id of event.userIds) this.unpluggedUserIds.add(id)
      for (const char of this.chars.values()) {
        if (char.isAgent) char.setUnplugged(this.unpluggedUserIds.has(char.userId))
      }
      return
    }
    if (event.kind === 'agentHr') {
      // Full set each emit: HR goes ON for listed agents and OFF for the rest.
      this.hrUserIds.clear()
      for (const id of event.userIds) this.hrUserIds.add(id)
      for (const char of this.chars.values()) {
        if (char.isAgent) char.setHrManager(this.hrUserIds.has(char.userId))
      }
      return
    }
    if (event.kind === 'agentPersonal') {
      // Full set each emit: teal for listed agents, the standard robot for the rest.
      this.personalUserIds.clear()
      for (const id of event.userIds) this.personalUserIds.add(id)
      for (const char of this.chars.values()) {
        if (char.isAgent) char.setPersonal(this.personalUserIds.has(char.userId))
      }
      return
    }
    if (event.kind === 'agentEa') {
      // Full set each emit: coral for listed agents, the standard robot for the rest.
      this.eaUserIds.clear()
      for (const id of event.userIds) this.eaUserIds.add(id)
      for (const char of this.chars.values()) {
        if (char.isAgent) char.setEa(this.eaUserIds.has(char.userId))
      }
      return
    }
    if (event.kind === 'agentSteward') {
      // Full sets each emit: the badge for Stewards, the cape for their staff
      // tier (a staff id that is not a Steward gets nothing), off for the rest.
      this.stewardUserIds.clear()
      this.stewardStaffUserIds.clear()
      for (const id of event.userIds) this.stewardUserIds.add(id)
      for (const id of event.staffUserIds) {
        if (this.stewardUserIds.has(id)) this.stewardStaffUserIds.add(id)
      }
      for (const char of this.chars.values()) {
        if (!char.isAgent) continue
        char.setSteward(
          this.stewardUserIds.has(char.userId),
          this.stewardStaffUserIds.has(char.userId),
        )
      }
      return
    }
    if (event.kind === 'officeHealth') {
      this.upsertRack(event.officeId, event.name, event.level, event.issues)
      return
    }
    if (event.kind === 'cameraFollow') {
      // Arranging: never glide/follow — camera motion mid-drag misplaces rooms.
      if (this.arrangeMode) return
      // Locate a person: glide to them, then soft-follow (breaks on manual
      // pan/zoom — Two Point Hospital's rule, follow never fights the user).
      const char = this.chars.get(event.userId)
      if (char) {
        const cam = this.cameras.main
        this.autoDirectorPause = this.time.now + 20000
        this.worldFits = false
        this.cameraGlide?.remove()
        const targetZoom = Math.max(cam.zoom, 0.9)
        const state = { z: cam.zoom, x: cam.midPoint.x, y: cam.midPoint.y }
        this.cameraGlide = this.tweens.add({
          targets: state,
          z: targetZoom,
          x: char.x,
          y: char.y,
          duration: 500,
          ease: 'Sine.easeInOut',
          onUpdate: () => {
            cam.setZoom(state.z)
            cam.centerOn(state.x, state.y)
          },
          onComplete: () => {
            cam.startFollow(char, true, 0.08, 0.08)
          },
        })
      }
      return
    }
    if (event.kind === 'cameraRoom') {
      const room = this.layout.roomById[event.pulseId]
      if (room) this.focusCameraOnRoom(room)
      return
    }
    if (event.kind === 'taskBoard') {
      const board = this.taskBoards.get(event.pulseId)
      if (board) {
        const prev = this.taskCounts.get(event.pulseId)
        const counts = {
          open: event.open,
          doing: event.doing,
          done: event.done,
          overdue: event.overdue,
        }
        this.taskCounts.set(event.pulseId, counts)
        this.drawTaskBoard(event.pulseId, board.x, board.y, counts)
        // A new pin pops the board — only when something actually changed.
        const changed =
          !prev ||
          prev.open !== counts.open ||
          prev.doing !== counts.doing ||
          prev.done !== counts.done ||
          prev.overdue !== counts.overdue
        if (changed && prev) {
          const pop = this.add
            .rectangle(board.x + 27, board.y + 9, 58, 24, 0xffffff, 0.18)
            .setDepth(6)
          this.tweens.add({
            targets: pop,
            alpha: 0,
            duration: 900,
            onComplete: () => pop.destroy(),
          })
        }
      }
      return
    }
    if (event.kind === 'notesShelf') {
      const shelf = this.noteShelves.get(event.pulseId)
      if (shelf && (shelf.count !== event.count || event.fresh)) {
        this.drawNotesShelf(
          event.pulseId,
          shelf.x,
          shelf.y,
          event.count,
          event.fresh,
        )
      }
      return
    }
    if (event.kind === 'reaction') {
      const char = this.chars.get(event.userId)
      char?.reactionPop(event.symbol)
      if (char) playSfx('reaction')
      const roomId = this.roomOf(event.userId)
      if (roomId) {
        this.boards
          .get(roomId)
          ?.push('reaction', char?.isAgent ? AGENT_DOT : HUMAN_DOT, event.symbol)
      }
      return
    }
    if (event.kind === 'insight') {
      const room = this.layout.roomById[event.pulseId]
      if (room) {
        this.insightFlash(room)
        this.boards.get(event.pulseId)?.push('insight', INSIGHT_DOT, '✦')
      }
      return
    }
    if (event.kind === 'task') {
      const room = this.layout.roomById[event.pulseId]
      if (!room) return
      const actor = event.actorId ? this.chars.get(event.actorId) : undefined
      if (event.action !== 'deleted') {
        playSfx('task')
        const created = event.action === 'created'
        // The actor visibly produces the work — token rises from them (agent
        // or human coloured), so you can see WHO did it, agents included.
        if (actor) {
          actor.reactionPop(created ? '📋' : '✓')
          this.spawnTaskTokenAt(actor.x, actor.y - 12, actor.isAgent)
        } else {
          this.spawnTaskToken(room)
        }
        this.boards
          .get(event.pulseId)
          ?.push(
            created ? 'task' : 'done',
            actor?.isAgent ? AGENT_DOT : TASK_DOT,
            created ? '+' : '✓',
          )
      } else {
        actor?.reactionPop('🗑')
      }
      // Assignees (other than the actor) acknowledge the hand-off.
      for (const userId of event.assigneeUserIds) {
        if (userId === event.actorId) continue
        const char = this.chars.get(userId)
        if (char) this.sendToRoom(char, room, () => char.say('on it'))
      }
      this.markActivity(actor?.x ?? room.x + room.w / 2, actor?.y ?? room.y + room.h / 2)
      return
    }
    if (event.kind === 'note') {
      // Realtime pulse-note broadcast: the author visibly writes — the shelf
      // itself redraws via the notesShelf refetch this event also triggers.
      const room = this.layout.roomById[event.pulseId]
      if (!room) return
      const actor = event.actorId ? this.chars.get(event.actorId) : undefined
      if (event.action !== 'deleted') actor?.reactionPop('📝')
      this.boards
        .get(event.pulseId)
        ?.push(
          'note',
          actor?.isAgent ? AGENT_DOT : HUMAN_DOT,
          event.action === 'deleted' ? '−' : '📝',
        )
      this.markActivity(
        actor?.x ?? room.x + room.w / 2,
        actor?.y ?? room.y + room.h / 2,
      )
    }
  }

  private roomOf(userId: string): string | undefined {
    return this.layout.byUser[userId]?.roomId
  }

  /** Ghost desks with a glowing "+ hire" tag — click to staff the room with an
   *  AI agent. Only drawn for users who can actually hire (org owners). */
  private drawHireSpots(): void {
    if (!this.canHire) return
    for (const spot of this.layout.hireSpots) {
      this.add
        .image(spot.x, spot.y + 10, deskTexKey(0))
        .setScale(1.25)
        .setDepth(9)
        .setAlpha(0.28)
      const tag = this.add
        .text(spot.x, spot.y - 2, '+ hire', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '9px',
          color: '#0f1116',
          backgroundColor: '#8f88ff',
          padding: { x: 5, y: 2 },
        })
        .setOrigin(0.5)
        .setDepth(12)
      this.tweens.add({
        targets: tag,
        alpha: { from: 1, to: 0.55 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })
    }
  }

  /** Briefly light up a topic pill when a message lands in that topic. */
  private flashTopic(roomId: string, topicId: string): void {
    const pill = this.topicPills.get(`${roomId}:${topicId}`)
    if (!pill) return
    pill.setColor('#ffd86b')
    this.tweens.add({
      targets: pill,
      scale: { from: 1, to: 1.35 },
      duration: 220,
      yoyo: true,
      repeat: 1,
      ease: 'Sine.inOut',
    })
    this.time.delayedCall(2400, () => pill.setColor(theme.terracottaLite))
  }

  /** A shared meeting room: a board strip (current + next meeting), a conference
   *  table backdrop, screen, plant, cooler. Attendees, if any, are seated over
   *  the table by spawnCharacters. The board owns the live/next status — there is
   *  no separate "● Live / Available" header line (the board replaces it). */
  private drawMeetingRoom(room: RoomLayout): void {
    const floorTop = room.y + HEADER + room.boardH
    const floorH = room.h - (floorTop - room.y) - 6
    const cx = room.x + room.w / 2
    const cy = floorTop + floorH / 2 + 6
    const baseD = 3

    // Board strip between the header and the floor.
    this.drawMeetingBoard(room)

    // Wall screen on the back wall, just under the board.
    this.add
      .image(cx, floorTop + 14, MEETING_TEX.screen)
      .setDepth(baseD)
      .setScale(Math.min(1, (room.w * 0.46) / 56))

    // Conference table (+8 baked chairs), spanning ~76% of the room floor.
    this.add
      .image(cx, cy, MEETING_TEX.table)
      .setDepth(baseD + 1)
      .setScale(Math.min((room.w * 0.76) / 132, (floorH - 18) / 64, 1))

    // Plant in the bottom-left corner.
    this.add
      .image(room.x + 18, room.y + room.h - 20, MEETING_TEX.plant)
      .setDepth(baseD + 2)
      .setScale(Math.min(1, floorH / 90))

    // The Zunou notetaker: when this meeting's companion bot is actually in
    // the meeting (or recording), it stands by the wall screen taking notes —
    // the meeting is visibly AI-attended.
    const companion = room.meeting?.companionStatus
    if (companion === 'in_meeting' || companion === 'recording') {
      const nx = room.x + room.w - 26
      const ny = floorTop + 34
      const bot = this.add.image(nx, ny, TEX.agent).setScale(1.9).setDepth(12)
      bot.setTint(0x9fe8ff)
      this.tweens.add({
        targets: bot,
        y: { from: ny, to: ny - 2 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })
      const tag = this.add
        .text(nx, ny + 14, companion === 'recording' ? '● notetaker' : '✎ notetaker', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '8.5px',
          color: companion === 'recording' ? '#ff8a8a' : '#9fe8ff',
          backgroundColor: '#10141aee',
          padding: { x: 4, y: 1 },
        })
        .setOrigin(0.5, 0)
        .setDepth(12)
      if (companion === 'recording') {
        this.tweens.add({
          targets: tag,
          alpha: { from: 1, to: 0.45 },
          duration: 900,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.inOut',
        })
      }
    }

    // Water cooler bottom-right (skip very small rooms).
    if (room.w > 150) {
      this.add
        .image(room.x + room.w - 16, room.y + room.h - 18, MEETING_TEX.cooler)
        .setDepth(baseD + 2)
        .setScale(Math.min(1, floorH / 90))
    }
  }

  /**
   * The per-room wall board under the header: the CURRENT live meeting in this
   * room (name + pulse · start–end · N in room) with a pulsing LED, or — when
   * idle — the NEXT upcoming session for this room/today ("NEXT · Retro · 14:00")
   * or a calm "Available". Reads straight off the snapshot; styling mirrors
   * RoomBoard's charcoal/steel panel. Built via Phaser primitives (the board is a
   * dynamic status panel, not a static furniture sprite).
   */
  private drawMeetingBoard(room: RoomLayout): void {
    const x = room.x + 10
    const y = room.y + HEADER - 4
    const w = room.w - 20
    const h = room.boardH - 2
    const now = Date.now()

    const g = this.add.graphics().setDepth(6)
    g.fillStyle(toInt('#0a0c11'), 1)
    g.lineStyle(1, toInt('#33384a'), 1)
    g.fillRoundedRect(x, y, w, h, 5)
    g.strokeRoundedRect(x, y, w, h, 5)

    const live =
      room.meeting && isMeetingLive(room.meeting, now) ? room.meeting : null

    if (live) {
      const led = this.add.circle(x + 9, y + h / 2 - 5, 3, toInt('#ffd86b')).setDepth(7)
      this.tweens.add({
        targets: led,
        alpha: { from: 0.35, to: 1 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })
      this.add
        .text(x + 18, y + 4, this.ellipsize(live.name, w - 26, 11), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: theme.cream,
        })
        .setDepth(7)

      const pulse = live.pulseId
        ? this.snapshot.pulses.find((p) => p.id === live.pulseId)
        : undefined
      const span = [fmtClock(live.startAt), fmtClock(live.endAt)]
        .filter(Boolean)
        .join('–')
      const seated = this.layout.placements.filter(
        (p) => p.roomId === room.id,
      ).length
      const parts: string[] = []
      if (pulse?.name) parts.push(pulse.name)
      if (span) parts.push(span)
      // Seated = the invite list; when the session knows who actually
      // CONNECTED, show that too ("3 in room · 1 joined").
      parts.push(`${seated} in room`)
      const joined = live.attendeeUserIds.length
      if (joined > 0 && joined < seated) parts.push(`${joined} joined`)
      this.add
        .text(x + 18, y + h - 4, this.ellipsize(parts.join('  ·  '), w - 24, 9), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '9px',
          color: '#9fd9ff',
        })
        .setOrigin(0, 1)
        .setDepth(7)
      return
    }

    // Idle room — show the next upcoming meeting FOR THIS ROOM'S PULSE, or
    // "Available". No org-wide fallback: it printed the same "NEXT · Standup"
    // on every idle room at once, which reads as a triple-booked bug.
    const next = upcomingForPulse(
      this.snapshot.meetings,
      room.meeting?.pulseId ?? null,
      now,
    )[0]
    if (next) {
      this.add
        .text(x + 9, y + 4, 'NEXT', {
          fontFamily: 'monospace',
          fontSize: '8px',
          color: '#5fd39a',
        })
        .setDepth(7)
      const when = fmtClock(next.startAt)
      const label = when ? `${next.name}  ·  ${when}` : next.name
      this.add
        .text(x + 9, y + h - 4, this.ellipsize(label, w - 18, 10), {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: theme.cream,
        })
        .setOrigin(0, 1)
        .setDepth(7)
    } else {
      const dot = this.add.circle(x + 9, y + h / 2, 2.5, toInt('#54d18c')).setDepth(7)
      dot.setAlpha(0.8)
      this.add
        .text(x + 18, y + h / 2, 'Available', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          color: theme.textDim,
        })
        .setOrigin(0, 0.5)
        .setDepth(7)
    }
  }

  /** Trim a label to fit `maxW` px at the given font size, adding an ellipsis.
   *  Measures with a throwaway Text so it stays font-accurate, then destroys it. */
  private ellipsize(text: string, maxW: number, sizePx: number): string {
    const probe = this.add
      .text(0, 0, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: `${sizePx}px`,
      })
      .setVisible(false)
    if (probe.width <= maxW) {
      probe.destroy()
      return text
    }
    let s = text
    while (s.length > 1 && probe.width > maxW) {
      s = s.slice(0, -1)
      probe.setText(`${s}…`)
    }
    const out = `${s}…`
    probe.destroy()
    return out
  }

  /** The ambient water-cooler lounge: a rug + two stools + the cooler sprite, in
   *  its own band below the floor. Static furniture; the people are choreographed
   *  by startWaterCooler. */
  private drawLounge(): void {
    const { cx, cy, w, coolerX, coolerY } = this.layout.lounge
    // Rug below character depth (10) so people stand ON it.
    this.add.image(cx, cy, LOUNGE_TEX.rug).setDepth(2)
    this.add
      .image(cx - w / 2 + 18, cy + 10, LOUNGE_TEX.stool)
      .setOrigin(0.5, 1)
      .setDepth(3)
    this.add
      .image(cx + w / 2 - 30, cy + 14, LOUNGE_TEX.stool)
      .setOrigin(0.5, 1)
      .setDepth(3)
    this.add
      .image(coolerX, coolerY, MEETING_TEX.cooler)
      .setOrigin(0.5, 1)
      .setDepth(4)
      .setScale(0.95)
    this.add
      .text(cx, cy - 50, '☕ Water Cooler', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '10px',
        color: theme.textDim,
      })
      .setOrigin(0.5)
      .setDepth(4)
  }

  /** The server corner: one pixel rack per office box, LEDs wired to the live
   *  onboarding checklist (ACTION = pulsing red, WARN = amber, OK = green).
   *  Racks appear only for org owners (the health query is gated) and sit by
   *  the lounge — the org's machine room, visible from the whole floor. */
  private upsertRack(
    officeId: string,
    name: string,
    level: 'OK' | 'WARN' | 'ACTION',
    issues: string[],
  ): void {
    const LED_COLOR = { OK: 0x54d18c, WARN: 0xe0a13a, ACTION: 0xff5a5a } as const
    const STATUS_TEXT = {
      OK: 'healthy',
      WARN: 'check setup',
      ACTION: 'needs attention',
    } as const
    const STATUS_COLOR = { OK: '#54d18c', WARN: '#e0a13a', ACTION: '#ff5a5a' } as const

    let rack = this.racks.get(officeId)
    if (!rack) {
      const { cx, cy, w } = this.layout.lounge
      const x = cx - w / 2 - 64 - this.rackCount * 52
      const y = cy + 16
      this.rackCount += 1

      const cabinet = this.add
        .rectangle(x, y, 24, 36, 0x14161d)
        .setOrigin(0.5, 1)
        .setStrokeStyle(1, 0x2a2e38)
        .setDepth(4)
      const leds: Phaser.GameObjects.Rectangle[] = []
      for (let i = 0; i < 3; i++) {
        this.add
          .rectangle(x - 1, y - 6 - i * 10, 16, 7, 0x0b0d12)
          .setOrigin(0.5, 1)
          .setDepth(5)
        leds.push(
          this.add
            .rectangle(x + 8, y - 9 - i * 10, 2.5, 2.5, LED_COLOR.OK)
            .setOrigin(0.5, 1)
            .setDepth(6),
        )
      }
      this.add
        .text(x, y + 4, `🖥 ${name.length > 14 ? `${name.slice(0, 13)}…` : name}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '9px',
          color: theme.textDim,
        })
        .setOrigin(0.5, 0)
        .setDepth(5)
      const status = this.add
        .text(x, y + 16, '', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '8.5px',
          color: STATUS_COLOR.OK,
        })
        .setOrigin(0.5, 0)
        .setDepth(5)
      // Click → the in-game office health card (full checklist + commands).
      cabinet.setInteractive({ useHandCursor: true })
      cabinet.setData('consumesClick', true)
      cabinet.on('pointerup', () => {
        this.onOfficeFocus?.(officeId)
      })
      rack = { leds, status }
      this.racks.set(officeId, rack)
    }

    rack.tween?.remove()
    rack.tween = undefined
    for (const led of rack.leds) {
      led.setFillStyle(LED_COLOR[level])
      led.setAlpha(1)
    }
    rack.status.setText(
      level === 'OK' ? STATUS_TEXT.OK : `${STATUS_TEXT[level]} · ${issues.length}`,
    )
    rack.status.setColor(STATUS_COLOR[level])
    if (level === 'ACTION') {
      rack.tween = this.tweens.add({
        targets: rack.leds,
        alpha: { from: 1, to: 0.2 },
        duration: 550,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.inOut',
      })
    }
  }

  /** Dress the org's "Agent Training" pulse room as the classroom: a header
   *  tag + a chalkboard on the wall. LEARNING agents wander in to study
   *  (startClassroom). */
  private decorateClassroom(room: RoomLayout): void {
    this.add
      .text(room.x + room.w - 8, room.y + 13, '🎓 classroom', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '9px',
        color: '#c9c3ff',
        backgroundColor: '#19163466',
        padding: { x: 4, y: 1 },
      })
      .setOrigin(1, 0.5)
      .setDepth(7)
    // Chalkboard: dark-green board + a few chalk scribbles, hung on the wall
    // strip between the live-feed board and the desks (right side, clear of
    // the desk grid so it never hides behind a sprite).
    const bx = room.x + room.w - 32
    const by = room.y + room.boardH + 30
    this.add
      .rectangle(bx, by, 44, 24, 0x1d3327)
      .setOrigin(0.5, 1)
      .setStrokeStyle(1.5, 0x5c4a32)
      .setDepth(4)
    for (let i = 0; i < 3; i++) {
      this.add
        .rectangle(bx - 14 + i * 3, by - 17 + i * 6, 24 - i * 6, 1.2, 0xe8e6df, 0.55)
        .setOrigin(0, 0.5)
        .setDepth(5)
    }
    this.add
      .rectangle(bx, by + 3, 10, 3, 0x8b93a7)
      .setOrigin(0.5, 1)
      .setDepth(4)
  }

  /** Opening a room glides the camera to it: the room fills the LEFT of the
   *  screen (the panel takes the right on desktop), comfortably zoomed. Driven
   *  by a plain scene tween (setZoom + centerOn each frame) — more reliable
   *  than the built-in camera effects, which were getting cut short. */
  private cameraGlide?: Phaser.Tweens.Tween
  private focusCameraOnRoom(room: RoomLayout): void {
    // Arranging: no glides — the whole mode's contract is a motionless camera.
    if (this.arrangeMode) return
    const cam = this.cameras.main
    cam.stopFollow()
    this.autoDirectorPause = this.time.now + 15000
    this.worldFits = false
    // Below 640 the panel is a bottom sheet — keep the room centred instead.
    // 640 matches the UI's compact breakpoint; the old 900 threshold left
    // 641-900px windows gliding the room underneath the floating panel.
    const panelW = cam.width >= 640 ? 360 : 0
    const zoom = Phaser.Math.Clamp(
      Math.min(
        (cam.width - panelW - 70) / room.w,
        (cam.height - 130) / room.h,
      ),
      this.minZoom,
      1.7,
    )
    const cx = room.x + room.w / 2 + panelW / (2 * zoom)
    const cy = room.y + room.h / 2
    this.cameraGlide?.remove()
    const state = { z: cam.zoom, x: cam.midPoint.x, y: cam.midPoint.y }
    this.cameraGlide = this.tweens.add({
      targets: state,
      z: zoom,
      x: cx,
      y: cy,
      duration: 480,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        cam.setZoom(state.z)
        cam.centerOn(state.x, state.y)
      },
    })
  }

  /** HUD zoom controls — zoom toward the screen centre, or reset to fit. */
  private zoomCamera(action: 'in' | 'out' | 'reset'): void {
    const cam = this.cameras.main
    cam.stopFollow()
    this.autoDirectorPause = this.time.now + 9000
    if (action === 'reset') {
      cam.setZoom(this.minZoom)
      cam.centerOn(
        this.stage.x + this.stage.w / 2,
        this.stage.y + this.stage.h / 2 - HUD_TOP / (2 * this.minZoom),
      )
      this.worldFits = true
      return
    }
    const factor = action === 'in' ? 1.35 : 1 / 1.35
    const cx = cam.width / 2
    const cy = cam.height / 2
    const before = cam.getWorldPoint(cx, cy)
    cam.setZoom(Phaser.Math.Clamp(cam.zoom * factor, this.minZoom, 2.4))
    const after = cam.getWorldPoint(cx, cy)
    cam.scrollX += before.x - after.x
    cam.scrollY += before.y - after.y
    // Keep the auto-director's gate honest for HUD zooms too (wheel/pinch
    // already set it via anchorZoom) — zooming in via "+" should wake it.
    this.worldFits = cam.zoom <= this.minZoom + 0.001
  }

  /** Tap a room where you have an empty chair → walk you over to your seat there
   *  and pan to it, then drift back home after a beat. Returns true if handled. */
  private goToMySeatAt(x: number, y: number): boolean {
    if (!this.selfUserId) return false
    const me = this.chars.get(this.selfUserId)
    if (!me) return false
    for (const room of this.layout.rooms) {
      if (x < room.x || x > room.x + room.w) continue
      if (y < room.y || y > room.y + room.h) continue
      // Only if you have an empty seat here (a room you belong to but aren't
      // currently sitting in).
      if (!this.vacantSeats.has(`${this.selfUserId}:${room.id}`)) return false
      this.autoDirectorPause = this.time.now + 12000
      // A leftover soft-follow (person "locate") would fight this pan and
      // snap the camera back to the followed person instead of your walk.
      this.cameras.main.stopFollow()
      this.cameras.main.pan(
        room.x + room.w / 2,
        room.y + room.h / 2,
        700,
        Phaser.Math.Easing.Sine.InOut,
      )
      this.relocateTo(me, room, () => {})
      return true
    }
    return false
  }

  private messageText(raw: string | null): string {
    if (!raw) return '…'
    try {
      const text = collectSlateText(JSON.parse(raw)).trim()
      if (text) return text
    } catch {
      // Not JSON — treat as a plain string body.
    }
    return raw
  }

  /** A per-user loose standing spot in a room — deterministic (hash of userId)
   *  so two people without reserved seats never stack on the same pixel. */
  private looseSpot(userId: string, room: RoomLayout): { x: number; y: number } {
    let h = 0
    for (let i = 0; i < userId.length; i += 1) h = (h * 31 + userId.charCodeAt(i)) >>> 0
    return {
      x: room.x + room.w / 2 + ((h % 49) - 24),
      y: room.y + room.h / 2 + (((h >> 6) % 33) - 16),
    }
  }

  private sendToRoom(char: Character, room: RoomLayout, onArrive: () => void): void {
    // Never pull a live-meeting attendee to another room (mirrors relocateTo):
    // ambient chatter, DMs, and task acks would visibly walk them out of the
    // conference mid-session. They act in place instead.
    const placement = this.layout.byUser[char.userId]
    if (placement?.atMeetingTable || placement?.meetingAtDesk) {
      onArrive()
      return
    }
    // Walk them to THEIR seat in that room if they have one — hiding the empty
    // chair while they're sitting in it — else to a loose spot near the middle.
    const key = `${char.userId}:${room.id}`
    const seat = this.seatPos.get(key) ?? this.looseSpot(char.userId, room)
    const tx = seat.x
    const ty = seat.y
    const marker = this.vacantSeats.get(key)
    // Mark busy so the water cooler won't yank them mid real-animation; cleared
    // when they head home. Real events always win (walkTo stops the prior tween).
    this.busyUsers.add(char.userId)
    char.walkTo(tx, ty, () => {
      // Hide the seat tag on ARRIVAL, not at walk start: an interrupted walk
      // (another event retargets them) never runs this callback, and a
      // hidden-at-start tag would stay invisible for the whole session.
      marker?.setVisible(false)
      onArrive()
      this.time.delayedCall(8000, () => {
        char.goHome()
        marker?.setVisible(true)
        this.busyUsers.delete(char.userId)
      })
    })
  }

  /** Move a character to a seat in `room` and KEEP them there — chatting in a
   *  room counts as visiting it, so they don't bounce back to their old seat.
   *  Frees the seat they left, claims the new one, and makes it their home. */
  private relocateTo(char: Character, room: RoomLayout, onArrive: () => void): void {
    const uid = char.userId
    // Never pull someone out of a live meeting — they just speak in place.
    const placement = this.layout.byUser[uid]
    if (placement?.atMeetingTable || placement?.meetingAtDesk) {
      onArrive()
      return
    }
    const prevRoom = this.currentRoom.get(uid) ?? placement?.roomId
    if (prevRoom === room.id) {
      // Already here — speak in place, no walk, no churn.
      onArrive()
      return
    }
    // Their old chair opens up again (only non-home seats carry a waiting tag).
    if (prevRoom) this.vacantSeats.get(`${uid}:${prevRoom}`)?.setVisible(true)

    // Take their reserved seat here if they have one, else a loose spot nearby.
    const key = `${uid}:${room.id}`
    const seat = this.seatPos.get(key) ?? this.looseSpot(uid, room)
    const tx = seat.x
    const ty = seat.y
    this.vacantSeats.get(key)?.setVisible(false)

    this.currentRoom.set(uid, room.id)
    if (placement) placement.roomId = room.id
    char.homeX = tx
    char.homeY = ty
    this.busyUsers.add(uid)
    char.walkTo(tx, ty, () => {
      this.busyUsers.delete(uid)
      onArrive()
    })
  }

  /** A task chip rising from whoever did the work (agent or human coloured). */
  private spawnTaskTokenAt(x: number, y: number, agent: boolean): void {
    const token = this.add.rectangle(x, y, 12, 9, agent ? AGENT_DOT : TASK_DOT)
    token.setStrokeStyle(1, 0x000000, 0.3)
    token.setDepth(20)
    this.tweens.add({
      targets: token,
      y: y - 26,
      alpha: { from: 1, to: 0 },
      duration: 1100,
      ease: 'Sine.out',
      onComplete: () => token.destroy(),
    })
  }

  private spawnTaskToken(room: RoomLayout): void {
    const token = this.add.rectangle(
      room.x + room.w / 2,
      room.y + 24,
      14,
      10,
      toInt(theme.task),
    )
    token.setDepth(20)
    this.tweens.add({
      targets: token,
      y: room.y + room.h - 26,
      duration: 600,
      ease: 'Bounce.out',
      onComplete: () => {
        this.tweens.add({
          targets: token,
          alpha: 0,
          delay: 1600,
          duration: 600,
          onComplete: () => token.destroy(),
        })
      },
    })
  }

  private insightFlash(room: RoomLayout): void {
    const x = room.x + room.w / 2
    const y = room.y - 4
    const bulb = this.add.circle(x, y, 7, toInt('#ffd86b'))
    bulb.setStrokeStyle(2, toInt('#e8a05a'))
    bulb.setDepth(40).setScale(0)
    const label = this.add
      .text(x, y - 14, 'insight', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '10px',
        color: '#ffd86b',
      })
      .setOrigin(0.5)
      .setDepth(40)
      .setAlpha(0)
    this.tweens.add({
      targets: bulb,
      scale: { from: 0, to: 1.2 },
      duration: 260,
      ease: 'Back.out',
      yoyo: true,
      hold: 700,
    })
    this.tweens.add({
      targets: label,
      alpha: { from: 0, to: 1 },
      y: y - 22,
      duration: 300,
      yoyo: true,
      hold: 800,
    })
    this.time.delayedCall(1700, () => {
      bulb.destroy()
      label.destroy()
    })
    this.markActivity(x, y)
  }

  private markActivity(x: number, y: number): void {
    this.lastActivity = { x, y, at: this.time.now }
  }

  private highlightHotRooms(): void {
    const pulseById = new Map(this.snapshot.pulses.map((p) => [p.id, p]))
    let hottest: { room: RoomLayout; heat: number } | undefined
    for (const room of this.layout.rooms) {
      if (room.category === 'LOBBY' || room.category === 'AGENTS') continue
      const pulse = pulseById.get(room.id)
      const tasks = this.snapshot.tasksByPulse[room.id]?.length ?? 0
      const heat = (pulse?.unreadCount ?? 0) + tasks * 10
      if (!hottest || heat > hottest.heat) hottest = { room, heat }
    }
    if (!hottest || hottest.heat < 20) return

    const r = hottest.room
    this.hottestCenter = { x: r.x + r.w / 2, y: r.y + r.h / 2 }
    const glow = this.add.graphics()
    glow.lineStyle(3, toInt('#e0703a'), 1)
    glow.strokeRoundedRect(r.x - 3, r.y - 3, r.w + 6, r.h + 6, 12)
    glow.setDepth(5)
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.25, to: 0.9 },
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    })
    this.add
      .text(r.x + r.w - 12, r.y + r.h - 8, 'busy', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '10px',
        color: '#e8a05a',
      })
      .setOrigin(1, 1)
  }

  private autoDirector(): void {
    if (this.worldFits) return
    // Arrange mode: the camera must hold still — a director pan mid-room-drag
    // shifts the world under the pointer and the room drops somewhere else.
    if (this.arrangeMode) return
    if (this.time.now < this.autoDirectorPause) return
    // While soft-following a located person, the director must stand down —
    // pan + follow fight each other and the camera yo-yos forever.
    if ((this.cameras.main as unknown as { _follow: unknown })._follow) return
    const recent =
      this.lastActivity !== undefined &&
      this.time.now - this.lastActivity.at <= 12000
    const target =
      recent && this.lastActivity ? this.lastActivity : this.hottestCenter
    if (target) {
      this.cameras.main.pan(
        target.x,
        target.y,
        1000,
        Phaser.Math.Easing.Sine.InOut,
      )
    }
  }
}
